import $ from 'jquery';
import _ from 'underscore';
import Backbone from "backbone";
import Chaplin from "chaplin";
import SequencerView from "views/audioEditor/SequencerView";
import TrebbleClientAPIHelper from "models/helper/TrebbleClientAPI";
import PlayerService from "services/PlayerService";
import PersistentModels from "services/PersistentModels";
import FileUploadHelper from "models/helper/FileUploadHelper";
import RolloutHelper from "models/helper/FeatureRolloutHelper";
import LocalStorageHelper from "models/helper/LocalStorageHelper";
import TrebbleAudio from "models/TrebbleAudio";
import TrebbleVideo from "models/TrebbleVideo";
import RecordedFilesConsumptionResult from "models/helper/RecordedFilesConsumptionResult";
import SequencerNodeCloner from "models/audioEditor/sequencerNodes/SequencerNodeCloner";
import SequencerNodeInspectorSectionController from "controllers/audioEditor/sequencerNodeInspectorSection/SequencerNodeInspectorSectionController";
import SequencerSettingsController from "controllers/audioEditor/SequencerSettingsController";
import SequencerNodeMoreOptionController from "controllers/audioEditor/SequencerNodeMoreOptionController";
import SoundEnhancerSettingsPopupContoller from "controllers/audioEditor/SoundEnhancerSettingsPopupContoller";
import SequencerAutomatedOperationsOptionController from "controllers/audioEditor/SequencerAutomatedOperationsOptionController";
import SequencerHelpContextOptionController from "controllers/audioEditor/SequencerHelpContextOptionController";
import SequencerViewConfigSettingsPopupContoller from "controllers/audioEditor/SequencerViewConfigSettingsPopupContoller";
import SequencerNodeUtils from 'models/audioEditor/SequencerNodeUtils';
import TrebbleNotificationManager from '../../components/common/NotificationManager';
import Utils from "models/helper/Utils";
import WrapNodeTypes from "models/audioEditor/sequencerNodes/WrapNodeTypes";
import Sequencer from "models/audioEditor/Sequencer";
import {
    areEditorWriteToolsEnabled,
    registerAudioEditorWebAgentBridge,
    releaseAudioEditorWebAgentBridge,
} from "models/audioEditor/agentBridge/AudioEditorWebAgentBridge";
import SequencerRendererHelper from "models/audioEditor/SequencerRendererHelper";
import SequencerExportHelper from "models/audioEditor/SequencerExportHelper";
import { VideoMediaImpl } from '../../components/timeloom/media/VideoMedia';
import { SceneItemImpl } from '../../components/timeloom/types/scene';
import ti18n from "i18n!nls/Sequenceri18n";
import guestTi18n from "i18n!nls/GuestFeaturei18n";
import RSVP from "rsvp";

const Rollout = RolloutHelper.getInstance();

const ALLOW_DUCKING_OF_BACKGROUND_MUSIC_IN_AUDIO_EDITOR = Rollout.isFeatureEnabled(Rollout.FEATURES.ALLOW_DUCKING_OF_BACKGROUND_MUSIC_IN_AUDIO_EDITOR, true);
const AUDIO_EDITOR_ONBOARDING = Rollout.isFeatureEnabled(Rollout.FEATURES.AUDIO_EDITOR_ONBOARDING, true);
const EDIT_VIDEO_FILES = Rollout.isFeatureEnabled(Rollout.FEATURES.EDIT_VIDEO_FILES, true);
const RENDER_VIDEO_IN_CLOUD = Rollout.isFeatureEnabled(Rollout.FEATURES.RENDER_VIDEO_IN_CLOUD, true);

/**
 * TRE-1205: the automation methods that stay reachable on production builds.
 *
 * This is an explicit allow-list, not a deny-list, so a newly added automation method is
 * contained by default — a mutating method someone forgets to classify simply won't be
 * exposed, whereas a forgotten entry on a deny-list would ship. The eight methods the web
 * agent bridge's read-only tools delegate to are all here, so the production bridge keeps
 * working. See docs/WEBMCP_EDITOR_TOOL_INVENTORY.md.
 */
const AUTOMATION_READ_ONLY_METHOD_NAMES = [
    "getMetadata",
    "getTranscriptSnapshot",
    "getSelection",
    "getHistoryState",
    "getSequencerSettingsSnapshot",
    "getClipboardState",
    "getRandomVisibleWordRange",
    "findText",
    "getNode",
    "getNodeSnapshots",
    "getRenderedAudioMetrics",
    "getPlaybackDebugState",
    "getVideoPreviewState",
    "getParagraphSnapshot"
];

const AUTOMATION_REGISTRY_ALWAYS_AVAILABLE_METHOD_NAMES = [
    "listEditors",
    "getEditor",
    "getActiveEditorId",
    "getActiveEditor",
    "getActiveTranscriptSnapshot",
    "getActiveSelection",
    "getActiveParagraphSnapshot"
];

function ensureAudioEditorAutomationRegistry() {
    if (typeof window === "undefined") {
        return null;
    }

    if (!window.trebble) {
        window.trebble = {};
    }

    if (!window.trebble.globalHelpers) {
        window.trebble.globalHelpers = {};
    }

    if (!window.trebble.globalHelpers.audioEditorAutomation) {
        window.trebble.globalHelpers.audioEditorAutomation = {
            _editorsById: {},
            _activeEditorId: null,

            listEditors: function() {
                return Object.keys(this._editorsById).map((editorId) => {
                    const editorApi = this._editorsById[editorId];
                    return editorApi && typeof editorApi.getMetadata === "function"
                        ? editorApi.getMetadata()
                        : { editorId };
                });
            },

            getEditor: function(editorId) {
                return editorId ? (this._editorsById[editorId] || null) : null;
            },

            getActiveEditorId: function() {
                return this._activeEditorId;
            },

            getActiveEditor: function() {
                return this.getEditor(this._activeEditorId);
            },

            getActiveTranscriptSnapshot: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getTranscriptSnapshot === "function"
                    ? activeEditor.getTranscriptSnapshot()
                    : null;
            },

            getTranscriptSnapshot: function() {
                return this.getActiveTranscriptSnapshot();
            },

            getActiveSelection: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getSelection === "function"
                    ? activeEditor.getSelection()
                    : null;
            },

            getSelection: function() {
                return this.getActiveSelection();
            },

            getActiveParagraphSnapshot: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getParagraphSnapshot === "function"
                    ? activeEditor.getParagraphSnapshot()
                    : null;
            },

            getParagraphSnapshot: function() {
                return this.getActiveParagraphSnapshot();
            },

            getHistoryState: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getHistoryState === "function"
                    ? activeEditor.getHistoryState()
                    : null;
            },

            getSequencerSettingsSnapshot: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getSequencerSettingsSnapshot === "function"
                    ? activeEditor.getSequencerSettingsSnapshot()
                    : null;
            },

            getClipboardState: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getClipboardState === "function"
                    ? activeEditor.getClipboardState()
                    : null;
            },

            getRandomVisibleWordRange: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getRandomVisibleWordRange === "function"
                    ? activeEditor.getRandomVisibleWordRange(options || {})
                    : null;
            },

            findText: function(queryText, options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.findText === "function"
                    ? activeEditor.findText(queryText, options || {})
                    : null;
            },

            getNode: function(nodeCid) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getNode === "function"
                    ? activeEditor.getNode(nodeCid)
                    : null;
            },

            getNodeSnapshots: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getNodeSnapshots === "function"
                    ? activeEditor.getNodeSnapshots()
                    : [];
            },

            getRenderedAudioMetrics: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getRenderedAudioMetrics === "function"
                    ? activeEditor.getRenderedAudioMetrics(options || {})
                    : null;
            },

            getVideoPreviewState: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getVideoPreviewState === "function"
                    ? activeEditor.getVideoPreviewState()
                    : null;
            },

            previewNode: function(nodeCid, options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.previewNode === "function"
                    ? activeEditor.previewNode(nodeCid, options || {})
                    : null;
            },

            selectText: function(queryText, options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.selectText === "function"
                    ? activeEditor.selectText(queryText, options || {})
                    : null;
            },

            selectVisibleWordRange: function(startWordIndex, endWordIndex, options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.selectVisibleWordRange === "function"
                    ? activeEditor.selectVisibleWordRange(startWordIndex, endWordIndex, options || {})
                    : null;
            },

            selectRandomVisibleWordRange: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.selectRandomVisibleWordRange === "function"
                    ? activeEditor.selectRandomVisibleWordRange(options || {})
                    : null;
            },

            selectNode: function(nodeCid, options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.selectNode === "function"
                    ? activeEditor.selectNode(nodeCid, options || {})
                    : null;
            },

            insertSyntheticMediaBeforeSelection: function(mediaKind, options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.insertSyntheticMediaBeforeSelection === "function"
                    ? activeEditor.insertSyntheticMediaBeforeSelection(mediaKind, options || {})
                    : null;
            },

            insertSyntheticMediaBulkBeforeSelection: function(mediaItems) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.insertSyntheticMediaBulkBeforeSelection === "function"
                    ? activeEditor.insertSyntheticMediaBulkBeforeSelection(mediaItems || [])
                    : null;
            },

            transcribeSelectedMedia: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.transcribeSelectedMedia === "function"
                    ? activeEditor.transcribeSelectedMedia(options || {})
                    : null;
            },

            setSelectedMediaVolume: function(mainVolumePercentage) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.setSelectedMediaVolume === "function"
                    ? activeEditor.setSelectedMediaVolume(mainVolumePercentage)
                    : null;
            },

            deleteSelectedSequencerNode: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.deleteSelectedSequencerNode === "function"
                    ? activeEditor.deleteSelectedSequencerNode()
                    : null;
            },

            deleteSelection: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.deleteSelection === "function"
                    ? activeEditor.deleteSelection()
                    : null;
            },

            restoreDeletedNode: function(nodeCid) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.restoreDeletedNode === "function"
                    ? activeEditor.restoreDeletedNode(nodeCid)
                    : null;
            },

            wrapSelectionWithMute: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.wrapSelectionWithMute === "function"
                    ? activeEditor.wrapSelectionWithMute()
                    : null;
            },

            deleteMuteWrapNode: function(nodeCid) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.deleteMuteWrapNode === "function"
                    ? activeEditor.deleteMuteWrapNode(nodeCid)
                    : null;
            },

            wrapSelectionWithMusic: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.wrapSelectionWithMusic === "function"
                    ? activeEditor.wrapSelectionWithMusic(options || {})
                    : null;
            },

            stubNextMusicPickerSelection: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.stubNextMusicPickerSelection === "function"
                    ? activeEditor.stubNextMusicPickerSelection(options || {})
                    : null;
            },

            stubNextRecordAndInsertUpload: function(mediaItems) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.stubNextRecordAndInsertUpload === "function"
                    ? activeEditor.stubNextRecordAndInsertUpload(mediaItems || [])
                    : null;
            },

            deleteMusicWrapNode: function(nodeCid) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.deleteMusicWrapNode === "function"
                    ? activeEditor.deleteMusicWrapNode(nodeCid)
                    : null;
            },

            moveNode: function(nodeCid, targetCid, placement) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.moveNode === "function"
                    ? activeEditor.moveNode(nodeCid, targetCid, placement)
                    : null;
            },

            moveWrapNode: function(nodeCid, targetCid, placement) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.moveWrapNode === "function"
                    ? activeEditor.moveWrapNode(nodeCid, targetCid, placement)
                    : null;
            },

            copySelection: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.copySelection === "function"
                    ? activeEditor.copySelection()
                    : null;
            },

            cutSelection: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.cutSelection === "function"
                    ? activeEditor.cutSelection()
                    : null;
            },

            pasteClipboardBeforeSelection: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.pasteClipboardBeforeSelection === "function"
                    ? activeEditor.pasteClipboardBeforeSelection()
                    : null;
            },

            undo: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.undo === "function"
                    ? activeEditor.undo()
                    : null;
            },

            redo: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.redo === "function"
                    ? activeEditor.redo()
                    : null;
            },

            saveProject: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.saveProject === "function"
                    ? activeEditor.saveProject()
                    : null;
            },

            applyParagraphBoundaryAction: function(actionName) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.applyParagraphBoundaryAction === "function"
                    ? activeEditor.applyParagraphBoundaryAction(actionName)
                    : null;
            },

            correctTextOnSelection: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.correctTextOnSelection === "function"
                    ? activeEditor.correctTextOnSelection(options || {})
                    : null;
            },

            assignSpeakerToSelection: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.assignSpeakerToSelection === "function"
                    ? activeEditor.assignSpeakerToSelection(options || {})
                    : null;
            },

            renameSelectedSpeaker: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.renameSelectedSpeaker === "function"
                    ? activeEditor.renameSelectedSpeaker(options || {})
                    : null;
            },

            createNewSpeakerAndAssignToSelection: function(options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.createNewSpeakerAndAssignToSelection === "function"
                    ? activeEditor.createNewSpeakerAndAssignToSelection(options || {})
                    : null;
            },

            setNoiseCancellation: function(on) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.setNoiseCancellation === "function"
                    ? activeEditor.setNoiseCancellation(on)
                    : null;
            },

            setSequencerSetting: function(settingKeyOrName, value) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.setSequencerSetting === "function"
                    ? activeEditor.setSequencerSetting(settingKeyOrName, value)
                    : null;
            },

            getPlaybackDebugState: function() {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.getPlaybackDebugState === "function"
                    ? activeEditor.getPlaybackDebugState()
                    : null;
            },

            seekVideoPreviewToTimeMs: function(timeMs, options) {
                const activeEditor = this.getActiveEditor();
                return activeEditor && typeof activeEditor.seekVideoPreviewToTimeMs === "function"
                    ? activeEditor.seekVideoPreviewToTimeMs(timeMs, options || {})
                    : null;
            }
        };

        if (!areEditorWriteToolsEnabled()) {
            const readOnlyRegistryMethodNames = AUTOMATION_READ_ONLY_METHOD_NAMES
                .concat(AUTOMATION_REGISTRY_ALWAYS_AVAILABLE_METHOD_NAMES);
            const registry = window.trebble.globalHelpers.audioEditorAutomation;
            Object.keys(registry).forEach(function(methodName) {
                if (
                    typeof registry[methodName] === "function"
                    && readOnlyRegistryMethodNames.indexOf(methodName) === -1
                ) {
                    delete registry[methodName];
                }
            });
        }
    }

    return window.trebble.globalHelpers.audioEditorAutomation;
}

const SequencerController = Chaplin.Controller.extend({

    initialize : function(options) {
        this.model =  new Sequencer();
        _.extend(this, Backbone.Events);
        this._jqueryPageElWhereSequencerWillBeAdded$el  = null;
        this._workspaceId = Object.prototype.hasOwnProperty.call(options || {}, "workspaceId")
            ? options.workspaceId
            : undefined;
        this.model.setWorkspaceId(this._workspaceId);
        this._projectAccessLevel = options && options.projectAccessLevel ? options.projectAccessLevel : null;
        this._workspaceReadOnly = options && options.workspaceReadOnly === true;
        this._activePresenceProjectId = null;
        this._hasMutationAccess = true;
        if(this._projectAccessLevel === "can_view" || this._workspaceReadOnly || LocalStorageHelper.getInstance().isWorkspaceReadOnly()){
            this.setMutationAccess(false);
        }
    },

    getModel : function() {
        return this.model;
    },

    getView : function() {
        return this.view;
    },

    setMutationAccess : function(hasMutationAccess){
        const hadMutationAccess = this.hasMutationAccess();
        this._hasMutationAccess = hasMutationAccess !== false;
        if(this.model && this.model.setMutationAccess){
            this.model.setMutationAccess(this._hasMutationAccess);
        }
        if(this.view && this.view.setMutationAccess){
            this.view.setMutationAccess(this._hasMutationAccess);
        }
        if(hadMutationAccess !== this.hasMutationAccess()){
            this.trigger("mutationAccessChange", this.hasMutationAccess());
        }
    },

    hasMutationAccess : function(){
        return this._hasMutationAccess !== false;
    },

    /**
     * Selects a created node if it should be auto-selected on creation.
     * This provides a consistent pattern for auto-selecting newly created nodes
     * so users can immediately configure them in the inspector.
     *
     * @param {SequencerNode} createdNode - The newly created node to potentially select
     * @param {boolean} [isInitialCreation=true] - Whether this is the initial creation
     *        (false for undo/redo, which should not trigger auto-select)
     */
    _selectCreatedNodeIfEnabled: function(createdNode, isInitialCreation) {
        // Default to initial creation if not specified
        if (isInitialCreation === undefined) {
            isInitialCreation = true;
        }

        // Only auto-select on initial creation, not undo/redo
        if (!isInitialCreation) {
            return;
        }

        // Check if the node wants to be auto-selected
        if (!createdNode || typeof createdNode.shouldAutoSelectOnCreation !== 'function') {
            return;
        }

        if (!createdNode.shouldAutoSelectOnCreation()) {
            return;
        }

        // Get the target node to select (for wrap nodes, may return start node)
        const targetNode = createdNode.getAutoSelectTarget ? createdNode.getAutoSelectTarget() : createdNode;

        // Use the view's selectSequencerNode method which properly:
        // 1. Updates _selectedNodeInfo with proper selection structure
        // 2. Triggers "onSelectionChange" event (listened by inspector and video preview)
        // 3. Calls setSelected(true) on the nodes
        // 4. Clears browser selection
        // 5. Shows context menu
        //
        // Previously we only called unselectCurrentlySelectedSequencerNodes() and
        // setSelected(true) directly on the model, which missed updating _selectedNodeInfo
        // and triggering "onSelectionChange" - causing inspector to not update.
        if (this.view) {
            this.view.selectSequencerNode(targetNode);

            // Scroll to the newly selected node so it's visible
            const nodeView = this.view.getSequencerNodeViewFromSequencerNode(targetNode);
            if (nodeView) {
                this.view.scrollToSequencerNodeIfneccessary(nodeView, false, true, true);
            }
        }

        // Get post-selection hint and trigger event for inspector
        const postSelectionHint = createdNode.getPostSelectionHint ? createdNode.getPostSelectionHint() : null;

        // Trigger event for inspector to handle post-selection behavior
        if (this._sequencerNodeInspectorSectionController && postSelectionHint) {
            this._sequencerNodeInspectorSectionController.trigger('nodeAutoSelected', {
                node: targetNode,
                hint: postSelectionHint
            });
        }
    },

    getAutomationEditorId: function() {
        return this.model ? this.model.cid : null;
    },

    getAutomationTranscriptSnapshot: function() {
        return this.view && typeof this.view.getAutomationTranscriptSnapshot === "function"
            ? this.view.getAutomationTranscriptSnapshot()
            : null;
    },

    getAutomationSelectionSnapshot: function() {
        return this.view && typeof this.view.getAutomationSelectionSnapshot === "function"
            ? this.view.getAutomationSelectionSnapshot()
            : null;
    },

    getAutomationParagraphSnapshot: function() {
        return this.view && typeof this.view.getAutomationParagraphSnapshot === "function"
            ? this.view.getAutomationParagraphSnapshot()
            : null;
    },

    getAutomationHistoryState: function() {
        const historyManagement = this.model && typeof this.model.getHistoryManagement === "function"
            ? this.model.getHistoryManagement()
            : null;
        const totalOperations = historyManagement && Array.isArray(historyManagement._operationArray)
            ? historyManagement._operationArray.length
            : 0;
        const indexOfNextOperationToUndo = historyManagement && typeof historyManagement._indexOfNextOperationToUndo === "number"
            ? historyManagement._indexOfNextOperationToUndo
            : -1;
        const nextUndoOperation = historyManagement && Array.isArray(historyManagement._operationArray)
            ? historyManagement._operationArray[indexOfNextOperationToUndo]
            : null;
        const nextRedoOperation = historyManagement && Array.isArray(historyManagement._operationArray)
            ? historyManagement._operationArray[indexOfNextOperationToUndo + 1]
            : null;
        const getOperationName = function(operationLog) {
            if (operationLog && typeof operationLog.getOperationName === "function") {
                return operationLog.getOperationName();
            }
            if (operationLog && operationLog.constructor && operationLog.constructor.name) {
                return operationLog.constructor.name;
            }
            return null;
        };

        return {
            canUndo: historyManagement && typeof historyManagement.canUndoOperation === "function"
                ? !!historyManagement.canUndoOperation()
                : false,
            canRedo: historyManagement && typeof historyManagement.canRedoOperation === "function"
                ? !!historyManagement.canRedoOperation()
                : false,
            undoDepth: Math.max(0, indexOfNextOperationToUndo + 1),
            totalOperations,
            nextUndoOperationName: getOperationName(nextUndoOperation),
            nextRedoOperationName: getOperationName(nextRedoOperation)
        };
    },

    getAutomationPlaybackPositionMs: function() {
        return this.model && typeof this.model.getCurrentPlaybackPositionMs === "function"
            ? this.model.getCurrentPlaybackPositionMs()
            : 0;
    },

    getAutomationPlaybackDebugState: function() {
        const activeVideoRenderer = this.model && typeof this.model.getActiveVideoRenderer === "function"
            ? this.model.getActiveVideoRenderer()
            : null;
        const videoContext = this.model && typeof this.model.getVideoContext === "function"
            ? this.model.getVideoContext()
            : null;
        const modernVideoRenderer = this.model && typeof this.model.getModernVideoRenderer === "function"
            ? this.model.getModernVideoRenderer()
            : null;

        return {
            isPlaying: !!(this.model && typeof this.model.isPlaying === "function" && this.model.isPlaying()),
            playbackPositionMs: this.getAutomationPlaybackPositionMs(),
            hasVideoSegment: !!(this.model && typeof this.model.getHasVideoSegment === "function" && this.model.getHasVideoSegment()),
            hasActiveVideoRenderer: !!activeVideoRenderer,
            activeVideoRendererType: activeVideoRenderer
                ? activeVideoRenderer.rendererType || (activeVideoRenderer.constructor ? activeVideoRenderer.constructor.name : null)
                : null,
            hasVideoContext: !!videoContext,
            hasModernVideoRenderer: !!modernVideoRenderer,
            viewHasVideoContext: !!(this.view && this.view._videoCtx),
            viewHasModernVideoRenderer: !!(this.view && this.view._modernVideoRenderer)
        };
    },

    _canSeekVideoRendererPreview: function(videoRenderer) {
        return !!videoRenderer && (
            typeof videoRenderer.seekAndRenderFrame === "function" ||
            typeof videoRenderer.seek === "function" ||
            (
                typeof videoRenderer.update === "function" &&
                typeof videoRenderer.currentTime !== "undefined"
            )
        );
    },

    _seekLegacyVideoContextPreviewToTimeMs: async function(videoRenderer, timeMs) {
        if (!videoRenderer ||
            typeof videoRenderer.update !== "function" ||
            typeof videoRenderer.currentTime === "undefined") {
            return false;
        }

        videoRenderer.currentTime = Math.max(0, timeMs) / 1000;
        videoRenderer.update(0);
        await new Promise((resolve) => {
            const scheduleFrame = typeof window !== "undefined" &&
                typeof window.requestAnimationFrame === "function"
                ? window.requestAnimationFrame.bind(window)
                : function(callback) {
                    return setTimeout(callback, 16);
                };
            scheduleFrame(function() {
                videoRenderer.update(0);
                resolve();
            });
        });
        return true;
    },

    _populateAutomationTimeloomPreviewTimeline: async function(videoRenderer) {
        if (!videoRenderer ||
            typeof videoRenderer.getTimeline !== "function" ||
            typeof videoRenderer.clearScenes !== "function") {
            return false;
        }

        const timeline = videoRenderer.getTimeline();
        if (!timeline || typeof timeline.addScene !== "function") {
            return false;
        }

        const utilsInstance = Utils.getInstance();
        let currentNode = this.model && typeof this.model.getFirstNode === "function"
            ? this.model.getFirstNode()
            : null;
        let cursorTimeMs = 0;
        let scenesAdded = 0;

        videoRenderer.clearScenes();
        timeline.absoluteStartTimeMs = 0;

        while(currentNode){
            const audioSegment = typeof currentNode.getAudioSegment === "function"
                ? currentNode.getAudioSegment()
                : null;
            const segmentStartMs = audioSegment && typeof audioSegment.getStartTime === "function"
                ? Number(audioSegment.getStartTime())
                : 0;
            const segmentEndMs = audioSegment && typeof audioSegment.getEndTime === "function"
                ? Number(audioSegment.getEndTime())
                : 0;
            const segmentDurationMs = Number.isFinite(segmentEndMs) &&
                Number.isFinite(segmentStartMs) &&
                segmentEndMs > segmentStartMs
                ? segmentEndMs - segmentStartMs
                : 0;

            if (utilsInstance.isVideoSequencerNodeInstance(currentNode) &&
                typeof currentNode.getVideo === "function") {
                const videoModel = currentNode.getVideo();
                const mediaUrl = videoModel && typeof videoModel.getAudioUrl === "function"
                    ? videoModel.getAudioUrl()
                    : null;
                const mediaDurationSeconds = videoModel && typeof videoModel.getDuration === "function"
                    ? Number(videoModel.getDuration())
                    : 0;
                const sceneDurationSeconds = segmentDurationMs > 0
                    ? segmentDurationMs / 1000
                    : Number.isFinite(mediaDurationSeconds) && mediaDurationSeconds > 0
                        ? mediaDurationSeconds
                        : 1;

                if (mediaUrl) {
                    const nodeId = currentNode.cid || `automation-video-${scenesAdded}`;
                    const videoMedia = new VideoMediaImpl({
                        id: `${nodeId}-media`,
                        type: 'video',
                        role: 'main',
                        src: mediaUrl,
                        offset: Number.isFinite(segmentStartMs) && segmentStartMs > 0
                            ? segmentStartMs / 1000
                            : 0,
                        duration: sceneDurationSeconds,
                        muted: true,
                        volume: 0,
                        usePartialDownload: false
                    });
                    timeline.addScene(new SceneItemImpl({
                        id: nodeId,
                        startAt: cursorTimeMs / 1000,
                        duration: sceneDurationSeconds,
                        layout: 'fullscreen',
                        inputs: [videoMedia],
                        objectFit: 'contain'
                    }));
                    scenesAdded++;
                }
            }

            cursorTimeMs += segmentDurationMs;
            currentNode = typeof currentNode.getNext === "function"
                ? currentNode.getNext()
                : null;
        }

        if (scenesAdded > 0 && typeof videoRenderer.refresh === "function") {
            await videoRenderer.refresh();
        }

        return scenesAdded > 0;
    },

    seekVideoPreviewToTimeMsForAutomation: async function(timeMs, options) {
        const numericTimeMs = Number(timeMs);
        const targetTimeMs = Number.isFinite(numericTimeMs)
            ? Math.max(0, numericTimeMs)
            : 0;
        const nextOptions = options || {};
        const seekIntent = nextOptions.seekIntent || "playback";
        const videoRenderer = this.model && typeof this.model.getActiveVideoRenderer === "function"
            ? this.model.getActiveVideoRenderer()
            : null;

        if (!videoRenderer) {
            return {
                success: false,
                reason: "no-video-renderer",
                requestedTimeMs: targetTimeMs,
                seekIntent
            };
        }

        if (typeof videoRenderer.populateTimeline === "function" &&
            typeof videoRenderer.seekAndRenderFrame === "function") {
            await videoRenderer.populateTimeline();
            await videoRenderer.seekAndRenderFrame(targetTimeMs, {
                skipPopulate: true,
                seekIntent,
                waitForVideoReadyTimeoutMs: nextOptions.waitForVideoReadyTimeoutMs
            });
        } else if (typeof videoRenderer.seekAndRenderFrame === "function") {
            await videoRenderer.seekAndRenderFrame(targetTimeMs, {
                seekIntent,
                waitForVideoReadyTimeoutMs: nextOptions.waitForVideoReadyTimeoutMs
            });
        } else if (typeof videoRenderer.seek === "function") {
            const didPopulateTimeloomPreview = typeof videoRenderer.getTimeline === "function" &&
                typeof videoRenderer.seekAndRenderFrame !== "function"
                ? await this._populateAutomationTimeloomPreviewTimeline(videoRenderer)
                : false;

            const timeline = typeof videoRenderer.getTimeline === "function"
                ? videoRenderer.getTimeline()
                : null;
            const timelineStartMs = timeline && typeof timeline.absoluteStartTimeMs === "number"
                ? timeline.absoluteStartTimeMs
                : 0;
            const rendererTimeMs = didPopulateTimeloomPreview
                ? Math.max(0, targetTimeMs - timelineStartMs)
                : targetTimeMs;

            videoRenderer.seek(rendererTimeMs, seekIntent);
            await new Promise((resolve) => {
                const scheduleFrame = typeof window !== "undefined" &&
                    typeof window.requestAnimationFrame === "function"
                    ? window.requestAnimationFrame.bind(window)
                    : function(callback) {
                        return setTimeout(callback, 16);
                    };
                scheduleFrame(function() {
                    scheduleFrame(resolve);
                });
            });
            if (!didPopulateTimeloomPreview && typeof videoRenderer.waitForVideoReady === "function") {
                await videoRenderer.waitForVideoReady(nextOptions.waitForVideoReadyTimeoutMs || nextOptions.timeoutMs || 5000);
            }
        } else if (await this._seekLegacyVideoContextPreviewToTimeMs(videoRenderer, targetTimeMs)) {
            // Legacy VideoContext exposes seeking through the currentTime setter.
        } else {
            return {
                success: false,
                reason: "renderer-cannot-seek",
                requestedTimeMs: targetTimeMs,
                seekIntent
            };
        }

        if (this.model && typeof this.model.isPlaying === "function" && !this.model.isPlaying()) {
            this.model.updatePlaybackPositionInMilliseconds(targetTimeMs);
        }

        if (this.view && typeof this.view.updateProgressBarPosition === "function") {
            this.view.updateProgressBarPosition(targetTimeMs);
        }

        return {
            success: true,
            requestedTimeMs: targetTimeMs,
            playbackPositionMs: this.getAutomationPlaybackPositionMs(),
            seekIntent
        };
    },

    getAutomationSequencerSettingsSnapshot: function() {
        const sequencerSettings = this.model && typeof this.model.getSequencerSettings === "function"
            ? this.model.getSequencerSettings()
            : null;
        const changeableSettings = sequencerSettings && typeof sequencerSettings.getChangeableSettings === "function"
            ? sequencerSettings.getChangeableSettings()
            : {};
        const settingValues = {};

        Object.keys(changeableSettings).forEach((settingKey) => {
            const settingName = changeableSettings[settingKey];
            settingValues[settingKey] = sequencerSettings && typeof sequencerSettings.getSetting === "function"
                ? sequencerSettings.getSetting(settingName)
                : undefined;
        });

        return {
            noiseCancellationOn: sequencerSettings && typeof sequencerSettings.isNoiseCancellationOn === "function"
                ? !!sequencerSettings.isNoiseCancellationOn()
                : false,
            magicSoundEnhancerOn: sequencerSettings && typeof sequencerSettings.isMagicSoundEnhancerOn === "function"
                ? !!sequencerSettings.isMagicSoundEnhancerOn()
                : false,
            rerenderRequiredOnNextPlayback: this.model && typeof this.model.isRerenderRequiredOnNexPlayback === "function"
                ? !!this.model.isRerenderRequiredOnNexPlayback()
                : false,
            audioEditVersion: this.model && typeof this.model.getAudioEditVersion === "function"
                ? this.model.getAudioEditVersion()
                : null,
            changeableSettings,
            settingValues
        };
    },

    getAutomationClipboardState: function() {
        const sequencerClipboard = this.model && typeof this.model.getSequencerClipboard === "function"
            ? this.model.getSequencerClipboard()
            : null;
        const clipboardStartNode = sequencerClipboard && typeof sequencerClipboard.getStartNodeInClipboard === "function"
            ? sequencerClipboard.getStartNodeInClipboard()
            : null;

        return {
            isEmpty: this.model && typeof this.model.isClipboardEmpty === "function"
                ? !!this.model.isClipboardEmpty()
                : !clipboardStartNode,
            startCid: clipboardStartNode ? clipboardStartNode.cid || null : null,
            startType: this._getAutomationNodeType(clipboardStartNode),
            text: this._getAutomationPlainNodeText(clipboardStartNode),
        };
    },

    getRandomAutomationVisibleWordRange: function(options) {
        return this.view && typeof this.view.getRandomAutomationVisibleWordRange === "function"
            ? this.view.getRandomAutomationVisibleWordRange(options || {})
            : null;
    },

    findAutomationText: function(queryText, options) {
        return this.view && typeof this.view.findAutomationVisibleWordRangeByText === "function"
            ? this.view.findAutomationVisibleWordRangeByText(queryText, options || {})
            : null;
    },

    _getAutomationNodeByCid: function(nodeCid) {
        return this.model && typeof this.model.getSequencerNodeByNodeCid === "function" && nodeCid
            ? this.model.getSequencerNodeByNodeCid(nodeCid)
            : null;
    },

    _getAutomationNodeType: function(sequencerNode) {
        if (!sequencerNode) {
            return null;
        }

        const constructorFn = typeof sequencerNode.getConstructor === "function"
            ? sequencerNode.getConstructor()
            : sequencerNode.constructor;

        return constructorFn && (constructorFn.CLASSNAME || constructorFn.name)
            ? (constructorFn.CLASSNAME || constructorFn.name)
            : null;
    },

    _getAutomationPlainNodeText: function(sequencerNode) {
        if (!sequencerNode || typeof sequencerNode.getContent !== "function") {
            return null;
        }

        const content = sequencerNode.getContent(false);
        return typeof content === "string" && content.indexOf("<") === -1
            ? content
            : null;
    },

    _canAutomationCopyCurrentSelection: function() {
        const currentSelectionInfo = this.view && typeof this.view.getSelectionNode === "function"
            ? this.view.getSelectionNode()
            : null;

        return !!(
            currentSelectionInfo
            && currentSelectionInfo.startSequencerNodeModel
            && currentSelectionInfo.endSequencerNodeModel
            && !Utils.getInstance().doesSelectedNodesContainOrphanWrapNode(
                currentSelectionInfo.startSequencerNodeModel,
                currentSelectionInfo.endSequencerNodeModel
            )
        );
    },

    _hasAutomationHistoryStateChanged: function(previousHistoryState, nextHistoryState) {
        const previousState = previousHistoryState || {};
        const nextState = nextHistoryState || {};

        return !!previousState.canUndo !== !!nextState.canUndo
            || !!previousState.canRedo !== !!nextState.canRedo
            || (typeof previousState.undoDepth === "number" ? previousState.undoDepth : 0)
                !== (typeof nextState.undoDepth === "number" ? nextState.undoDepth : 0)
            || (typeof previousState.totalOperations === "number" ? previousState.totalOperations : 0)
                !== (typeof nextState.totalOperations === "number" ? nextState.totalOperations : 0);
    },

    _canRunAutomationMutation: function() {
        return !(typeof this.hasMutationAccess === "function" && !this.hasMutationAccess());
    },

    _serializeAutomationNode: function(sequencerNode) {
        if (!sequencerNode) {
            return null;
        }

        const snapshot = {
            cid: sequencerNode.cid || null,
            type: this._getAutomationNodeType(sequencerNode),
            text: this._getAutomationPlainNodeText(sequencerNode),
            isSelected: typeof sequencerNode.isSelected === "function"
                ? !!sequencerNode.isSelected()
                : false,
            isWrapNode: typeof sequencerNode.isWrapNode === "function"
                ? !!sequencerNode.isWrapNode()
                : false
        };
        const utilsInstance = Utils.getInstance();
        const isAudioNode = utilsInstance.isAudioSequencerNodeInstance(sequencerNode);
        const isVideoNode = utilsInstance.isVideoSequencerNodeInstance(sequencerNode);
        const mediaModel = isVideoNode && typeof sequencerNode.getVideo === "function"
            ? sequencerNode.getVideo()
            : isAudioNode && typeof sequencerNode.getAudio === "function"
                ? sequencerNode.getAudio()
                : null;

        if (isAudioNode || isVideoNode) {
            snapshot.mediaKind = isVideoNode ? "video" : "audio";
            snapshot.title = mediaModel && typeof mediaModel.getTitle === "function"
                ? mediaModel.getTitle()
                : null;
            snapshot.mediaId = mediaModel && typeof mediaModel.getAudioId === "function"
                ? mediaModel.getAudioId()
                : null;
            snapshot.mediaUrl = mediaModel && typeof mediaModel.getAudioUrl === "function"
                ? mediaModel.getAudioUrl()
                : null;
            snapshot.mainVolumePercentage = typeof sequencerNode.getMainVolumePercentage === "function"
                ? sequencerNode.getMainVolumePercentage()
                : null;
            snapshot.introPaddingInMilliseconds = typeof sequencerNode.getIntroPaddingInMilliseconds === "function"
                ? sequencerNode.getIntroPaddingInMilliseconds()
                : null;
            snapshot.outroPaddingInMilliseconds = typeof sequencerNode.getOutroPaddingInMilliseconds === "function"
                ? sequencerNode.getOutroPaddingInMilliseconds()
                : null;
            snapshot.introFadeInMilliseconds = typeof sequencerNode.getIntroFadeInMilliseconds === "function"
                ? sequencerNode.getIntroFadeInMilliseconds()
                : null;
            snapshot.outroFadeInMilliseconds = typeof sequencerNode.getOutroFadeInMilliseconds === "function"
                ? sequencerNode.getOutroFadeInMilliseconds()
                : null;
        }

        if (isVideoNode && mediaModel) {
            snapshot.width = typeof mediaModel.getWidth === "function"
                ? mediaModel.getWidth()
                : null;
            snapshot.height = typeof mediaModel.getHeight === "function"
                ? mediaModel.getHeight()
                : null;
        }

        if (typeof sequencerNode.getParentStartWrapNode === "function") {
            const parentStartWrapNode = sequencerNode.getParentStartWrapNode();
            snapshot.parentStartWrapCid = parentStartWrapNode ? parentStartWrapNode.cid : null;
        }

        if (typeof sequencerNode.getStartWrapSequencerNode === "function") {
            const startWrapNode = sequencerNode.getStartWrapSequencerNode();
            snapshot.startWrapCid = startWrapNode ? startWrapNode.cid : null;
        }

        if (typeof sequencerNode.getEndWrapSequencerNode === "function") {
            const endWrapNode = sequencerNode.getEndWrapSequencerNode();
            snapshot.endWrapCid = endWrapNode ? endWrapNode.cid : null;
        }

        return snapshot;
    },

    getAutomationRenderedAudioMetrics: async function(options) {
        options = options || {};
        const startNode = options.startNodeCid
            ? this._getAutomationNodeByCid(options.startNodeCid)
            : null;
        const endNode = options.endNodeCid
            ? this._getAutomationNodeByCid(options.endNodeCid)
            : startNode;
        const shouldRenderClip = !!(startNode && endNode);
        const renderWavFile = options.renderWavFile !== false;
        const asVideo = false;
        const progressReportFunction = null;
        const alreadyRenderedAudioWAVBlob = null;
        const shouldSequencerNodeBePlayedValidatorFunction = null;
        const includeDeletedSegmentsWhenPossible = false;
        const skipAudioProcessing = options.skipAudioProcessing !== false;
        const applyMasterProcessing = options.applyMasterProcessing !== false;
        const frequencyBinsHz = Array.isArray(options.frequencyBinsHz)
            ? options.frequencyBinsHz.filter((frequencyHz) => typeof frequencyHz === "number" && frequencyHz > 0)
            : [];

        if ((options.startNodeCid && !startNode) || (options.endNodeCid && !endNode)) {
            throw new Error("Cannot render audio metrics because one or more requested sequencer nodes were not found.");
        }

        let renderedAudioBlob = null;
        if (shouldRenderClip && applyMasterProcessing) {
            renderedAudioBlob = await this.model.getRenderedAudioBlobClip(
                renderWavFile,
                startNode,
                endNode,
                asVideo,
                progressReportFunction,
                alreadyRenderedAudioWAVBlob,
                shouldSequencerNodeBePlayedValidatorFunction,
                includeDeletedSegmentsWhenPossible,
                skipAudioProcessing
            );
        } else if (shouldRenderClip) {
            renderedAudioBlob = await SequencerRendererHelper.getInstance().getRenderedAudioBlobClip(
                this.model,
                applyMasterProcessing,
                renderWavFile,
                startNode,
                endNode,
                asVideo,
                progressReportFunction,
                alreadyRenderedAudioWAVBlob,
                shouldSequencerNodeBePlayedValidatorFunction,
                includeDeletedSegmentsWhenPossible,
                skipAudioProcessing
            );
        } else if (applyMasterProcessing) {
            renderedAudioBlob = await this.model.getRenderedAudioBlob(
                renderWavFile,
                asVideo,
                progressReportFunction,
                alreadyRenderedAudioWAVBlob,
                skipAudioProcessing
            );
        } else {
            renderedAudioBlob = await SequencerRendererHelper.getInstance().getRenderedAudioBlob(
                this.model,
                applyMasterProcessing,
                renderWavFile,
                asVideo,
                progressReportFunction,
                alreadyRenderedAudioWAVBlob,
                shouldSequencerNodeBePlayedValidatorFunction,
                includeDeletedSegmentsWhenPossible,
                skipAudioProcessing
            );
        }

        return this._getAutomationAudioMetricsForBlob(renderedAudioBlob, {
            renderedScope: shouldRenderClip ? "clip" : "full",
            startNodeCid: startNode ? startNode.cid : null,
            endNodeCid: endNode ? endNode.cid : null,
            frequencyBinsHz
        });
    },

    previewAutomationNode: function(nodeCid, options) {
        const sequencerNode = this._getAutomationNodeByCid(nodeCid);
        const normalizedOptions = options || {};
        const stopAfterMilliseconds = typeof normalizedOptions.stopAfterMilliseconds === "number"
            ? normalizedOptions.stopAfterMilliseconds
            : 750;

        if (!sequencerNode || !this.model || typeof this.model.playPauseSequencerRenderingFromNode !== "function") {
            return RSVP.Promise.resolve({
                didPreview: false,
                node: this._serializeAutomationNode(sequencerNode),
                hasVideo: this.model && typeof this.model.getHasVideoSegment === "function"
                    ? this.model.getHasVideoSegment()
                    : false,
                activeVideoRendererAvailable: false
            });
        }

        return this.model.playPauseSequencerRenderingFromNode(
            sequencerNode,
            true,
            stopAfterMilliseconds,
            sequencerNode,
            null,
            false
        ).then((function() {
            return {
                didPreview: true,
                node: this._serializeAutomationNode(sequencerNode),
                hasVideo: typeof this.model.getHasVideoSegment === "function"
                    ? this.model.getHasVideoSegment()
                    : false,
                activeVideoRendererAvailable: !!(
                    this.model
                    && typeof this.model.getActiveVideoRenderer === "function"
                    && this.model.getActiveVideoRenderer()
                )
            };
        }).bind(this));
    },

    getAutomationVideoPreviewState: function() {
        const activeRenderer = this.model && typeof this.model.getActiveVideoRenderer === "function"
            ? this.model.getActiveVideoRenderer()
            : null;

        return {
            hasVideo: this.model && typeof this.model.getHasVideoSegment === "function"
                ? this.model.getHasVideoSegment()
                : false,
            activeVideoRendererAvailable: !!activeRenderer,
            rendererType: activeRenderer && activeRenderer.rendererType ? activeRenderer.rendererType : null,
            timeloomRendererState: this.model && typeof this.model.getTimeloomRendererState === "function"
                ? this.model.getTimeloomRendererState()
                : null
        };
    },

    _getAutomationAudioMetricsForBlob: async function(audioBlob, metadata) {
        if (!audioBlob) {
            throw new Error("Cannot calculate audio metrics because rendering returned no audio blob.");
        }

        const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextConstructor) {
            throw new Error("Cannot calculate audio metrics because AudioContext is unavailable.");
        }

        const audioContext = new AudioContextConstructor();
        try {
            const audioArrayBuffer = await audioBlob.arrayBuffer();
            const decodedAudioBuffer = await audioContext.decodeAudioData(audioArrayBuffer.slice(0));
            let sumSquares = 0;
            let peak = 0;
            let sampleCount = 0;
            let nonSilentSampleCount = 0;
            const silenceThreshold = 0.0005;
            const frequencyMagnitudes = {};

            for (let channelIndex = 0; channelIndex < decodedAudioBuffer.numberOfChannels; channelIndex++) {
                const channelData = decodedAudioBuffer.getChannelData(channelIndex);
                for (let sampleIndex = 0; sampleIndex < channelData.length; sampleIndex++) {
                    const absoluteSample = Math.abs(channelData[sampleIndex]);
                    sumSquares += absoluteSample * absoluteSample;
                    peak = Math.max(peak, absoluteSample);
                    sampleCount++;
                    if (absoluteSample > silenceThreshold) {
                        nonSilentSampleCount++;
                    }
                }
            }

            if (metadata.frequencyBinsHz && metadata.frequencyBinsHz.length > 0 && decodedAudioBuffer.length > 0) {
                const channelData = decodedAudioBuffer.getChannelData(0);
                metadata.frequencyBinsHz.forEach((frequencyHz) => {
                    const normalizedFrequency = frequencyHz / decodedAudioBuffer.sampleRate;
                    const coefficient = 2 * Math.cos(2 * Math.PI * normalizedFrequency);
                    let q0 = 0;
                    let q1 = 0;
                    let q2 = 0;

                    for (let sampleIndex = 0; sampleIndex < channelData.length; sampleIndex++) {
                        q0 = coefficient * q1 - q2 + channelData[sampleIndex];
                        q2 = q1;
                        q1 = q0;
                    }

                    frequencyMagnitudes[String(frequencyHz)] = Math.sqrt(
                        q1 * q1 + q2 * q2 - q1 * q2 * coefficient
                    ) / channelData.length;
                });
            }

            return {
                ...metadata,
                blobSize: audioBlob.size,
                durationSeconds: decodedAudioBuffer.duration,
                sampleRate: decodedAudioBuffer.sampleRate,
                numberOfChannels: decodedAudioBuffer.numberOfChannels,
                sampleCount,
                rms: sampleCount > 0 ? Math.sqrt(sumSquares / sampleCount) : 0,
                peak,
                nonSilentRatio: sampleCount > 0 ? nonSilentSampleCount / sampleCount : 0,
                frequencyMagnitudes
            };
        } finally {
            if (typeof audioContext.close === "function") {
                audioContext.close();
            }
        }
    },

    getAutomationNodeSnapshot: function(nodeCid) {
        return this._serializeAutomationNode(this._getAutomationNodeByCid(nodeCid));
    },

    getAutomationNodeSnapshots: function() {
        const snapshots = [];
        let currentNode = this.model && typeof this.model.getFirstNode === "function"
            ? this.model.getFirstNode()
            : null;

        while(currentNode){
            snapshots.push(this._serializeAutomationNode(currentNode));
            currentNode = typeof currentNode.getNext === "function"
                ? currentNode.getNext()
                : null;
        }

        return snapshots;
    },

    _getAutomationSequencerNodeIndex: function(sequencerNode) {
        let currentNode = this.model && typeof this.model.getFirstNode === "function"
            ? this.model.getFirstNode()
            : null;
        let index = 0;

        while(currentNode){
            if (currentNode === sequencerNode) {
                return index;
            }
            currentNode = typeof currentNode.getNext === "function"
                ? currentNode.getNext()
                : null;
            index++;
        }

        return -1;
    },

    _getAutomationMoveNeighbors: function(targetNode, placement) {
        if (placement !== "before" && placement !== "after") {
            throw new Error("Unsupported automation move placement: " + placement);
        }

        const newPreviousSequencerNode = placement === "after"
            ? targetNode
            : targetNode && typeof targetNode.getPrevious === "function"
                ? targetNode.getPrevious()
                : null;
        const newNextSequencerNode = placement === "after"
            ? targetNode && typeof targetNode.getNext === "function"
                ? targetNode.getNext()
                : null
            : targetNode;

        return {
            placement,
            newPreviousSequencerNode,
            newNextSequencerNode
        };
    },

    moveAutomationNode: function(nodeCid, targetCid, placement) {
        const nodeToMove = this._getAutomationNodeByCid(nodeCid);
        const targetNode = this._getAutomationNodeByCid(targetCid);
        const historyBeforeMove = this.getAutomationHistoryState();
        if (!this._canRunAutomationMutation()) {
            return {
                didMove: false,
                placement,
                node: this._serializeAutomationNode(nodeToMove),
                targetNode: this._serializeAutomationNode(targetNode),
                previousNode: null,
                nextNode: null,
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeMove,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const moveNeighbors = this._getAutomationMoveNeighbors(targetNode, placement);
        const canMove = !!(
            nodeToMove
            && targetNode
            && nodeToMove !== targetNode
            && moveNeighbors.newPreviousSequencerNode !== nodeToMove
            && moveNeighbors.newNextSequencerNode !== nodeToMove
            && this.model
            && typeof this.model.moveNode === "function"
        );

        if (canMove) {
            this.model.moveNode(
                nodeToMove,
                moveNeighbors.newPreviousSequencerNode,
                moveNeighbors.newNextSequencerNode
            );
        }

        const historyAfterMove = this.getAutomationHistoryState();

        return {
            didMove: canMove && this._hasAutomationHistoryStateChanged(historyBeforeMove, historyAfterMove),
            placement: moveNeighbors.placement,
            node: this._serializeAutomationNode(nodeToMove),
            targetNode: this._serializeAutomationNode(targetNode),
            previousNode: this._serializeAutomationNode(moveNeighbors.newPreviousSequencerNode),
            nextNode: this._serializeAutomationNode(moveNeighbors.newNextSequencerNode),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterMove,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    moveAutomationWrapNode: function(nodeCid, targetCid, placement) {
        const wrapNodeToMove = this._getAutomationNodeByCid(nodeCid);
        const targetNode = this._getAutomationNodeByCid(targetCid);
        const historyBeforeMove = this.getAutomationHistoryState();
        if (!this._canRunAutomationMutation()) {
            return {
                didMove: false,
                placement,
                node: this._serializeAutomationNode(wrapNodeToMove),
                targetNode: this._serializeAutomationNode(targetNode),
                previousNode: null,
                nextNode: null,
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeMove,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const moveNeighbors = this._getAutomationMoveNeighbors(targetNode, placement);
        const canMove = !!(
            wrapNodeToMove
            && targetNode
            && wrapNodeToMove !== targetNode
            && moveNeighbors.newPreviousSequencerNode !== wrapNodeToMove
            && moveNeighbors.newNextSequencerNode !== wrapNodeToMove
            && this.model
            && typeof this.model.moveWrapNode === "function"
        );
        const didMove = canMove
            ? !!this.model.moveWrapNode(
                wrapNodeToMove,
                this._getAutomationSequencerNodeIndex(wrapNodeToMove),
                this._getAutomationSequencerNodeIndex(targetNode),
                moveNeighbors.newPreviousSequencerNode,
                moveNeighbors.newNextSequencerNode
            )
            : false;
        const historyAfterMove = this.getAutomationHistoryState();

        return {
            didMove: didMove && this._hasAutomationHistoryStateChanged(historyBeforeMove, historyAfterMove),
            placement: moveNeighbors.placement,
            node: this._serializeAutomationNode(wrapNodeToMove),
            targetNode: this._serializeAutomationNode(targetNode),
            previousNode: this._serializeAutomationNode(moveNeighbors.newPreviousSequencerNode),
            nextNode: this._serializeAutomationNode(moveNeighbors.newNextSequencerNode),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterMove,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    getAutomationMetadata: function() {
        const transcriptSnapshot = this.getAutomationTranscriptSnapshot();

        return {
            editorId: this.getAutomationEditorId(),
            projectId: this.getProjectId ? this.getProjectId() : null,
            hasVideo: this.getHasVideoSegment ? this.getHasVideoSegment() : false,
            wordCount: transcriptSnapshot ? transcriptSnapshot.wordCount : 0,
            // TRE-1205: derive from the automation API this build actually exposes, so a
            // production (read-only) build never advertises deleteSelection/saveProject to an
            // agent that cannot call them. The previous hardcoded list had also already
            // drifted, omitting getPlaybackDebugState and seekVideoPreviewToTimeMs.
            supportedActions: Object.keys(this._createAutomationApi())
        };
    },

    _createAutomationMediaModel: function(mediaKind, options) {
        const normalizedOptions = options || {};
        const isVideo = mediaKind === "video";
        const createdAt = new Date().toISOString();
        const mediaJson = {
            audioId: normalizedOptions.mediaId || `automation-${mediaKind}-${Date.now()}`,
            title: normalizedOptions.title || `Automation ${mediaKind}`,
            artist: normalizedOptions.artist || "Trebble E2E",
            creationDate: normalizedOptions.creationDate || createdAt,
            audioType: normalizedOptions.audioType || "automationFixture",
            active: true,
            canBeDeleted: true,
            canBeFavorited: false,
            duration: typeof normalizedOptions.duration === "number" ? normalizedOptions.duration : 1,
            audioUrl: normalizedOptions.audioUrl || normalizedOptions.mediaUrl || "",
            imageArtUrl: normalizedOptions.imageArtUrl || null,
            isVideo
        };

        if (isVideo) {
            mediaJson.width = normalizedOptions.width || 160;
            mediaJson.height = normalizedOptions.height || 90;
            mediaJson.thumbnailWidth = normalizedOptions.thumbnailWidth || mediaJson.width;
            mediaJson.thumbnailHeight = normalizedOptions.thumbnailHeight || mediaJson.height;
            mediaJson.thumbnail = normalizedOptions.thumbnail || null;
            return new TrebbleVideo(mediaJson);
        }

        return new TrebbleAudio(mediaJson);
    },

    insertAutomationSyntheticMediaBeforeSelection: function(mediaKind, options) {
        const normalizedMediaKind = mediaKind === "video" ? "video" : "audio";
        const selectionInfo = this.view && typeof this.view.getSelectionNode === "function"
            ? this.view.getSelectionNode()
            : null;
        const targetNode = selectionInfo && selectionInfo.startSequencerNodeModel
            ? selectionInfo.startSequencerNodeModel
            : null;
        const historyBeforeInsert = this.getAutomationHistoryState();
        if (!this._canRunAutomationMutation()) {
            return {
                didInsert: false,
                mediaKind: normalizedMediaKind,
                node: null,
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeInsert,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const mediaModel = this._createAutomationMediaModel(normalizedMediaKind, options || {});
        const createdNode = targetNode
            ? normalizedMediaKind === "video"
                ? this.insertVideoMode(targetNode, true, mediaModel)
                : this.insertAudioMode(targetNode, true, mediaModel)
            : null;
        if (createdNode && this.view && typeof this.view.selectSequencerNode === "function") {
            this.view.selectSequencerNode(createdNode, true);
        }
        const historyAfterInsert = this.getAutomationHistoryState();

        return {
            didInsert: !!createdNode && this._hasAutomationHistoryStateChanged(historyBeforeInsert, historyAfterInsert),
            mediaKind: normalizedMediaKind,
            node: this._serializeAutomationNode(createdNode),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterInsert,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    insertAutomationSyntheticMediaBulkBeforeSelection: async function(mediaItems) {
        const selectionInfo = this.view && typeof this.view.getSelectionNode === "function"
            ? this.view.getSelectionNode()
            : null;
        const targetNode = selectionInfo && selectionInfo.startSequencerNodeModel
            ? selectionInfo.startSequencerNodeModel
            : null;
        const normalizedItems = Array.isArray(mediaItems) ? mediaItems : [];
        if (!this._canRunAutomationMutation()) {
            return {
                didInsert: false,
                nodes: [],
                selection: this.getAutomationSelectionSnapshot(),
                history: this.getAutomationHistoryState(),
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const mediaModels = normalizedItems.map((mediaItem) => {
            const normalizedMediaItem = mediaItem || {};
            const mediaKind = normalizedMediaItem.mediaKind === "video" ? "video" : "audio";
            return this._createAutomationMediaModel(mediaKind, normalizedMediaItem.options || normalizedMediaItem);
        });
        const historyBeforeInsert = this.getAutomationHistoryState();
        const createdNodes = targetNode && mediaModels.length
            ? await this.model.insertAudioOrVideoNodesInBulk(targetNode, true, mediaModels)
            : [];

        if (createdNodes && createdNodes[0] && this.view && typeof this.view.selectSequencerNode === "function") {
            this.view.selectSequencerNode(createdNodes[0], true);
        }

        const historyAfterInsert = this.getAutomationHistoryState();
        const serializedNodes = (createdNodes || []).map((createdNode) => {
            return this._serializeAutomationNode(createdNode);
        });

        return {
            didInsert: serializedNodes.length === mediaModels.length
                && this._hasAutomationHistoryStateChanged(historyBeforeInsert, historyAfterInsert),
            nodes: serializedNodes,
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterInsert,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    transcribeAutomationSelectedMedia: async function(options) {
        const normalizedOptions = options || {};
        const selectionInfo = this.view && typeof this.view.getSelectionNode === "function"
            ? this.view.getSelectionNode()
            : null;
        const selectedNode = selectionInfo && selectionInfo.startSequencerNodeModel === selectionInfo.endSequencerNodeModel
            ? selectionInfo.startSequencerNodeModel
            : null;
        const historyBeforeTranscribe = this.getAutomationHistoryState();
        const canTranscribe = !!(
            selectedNode
            && this.model
            && typeof this.model.transcribeSequencerNode === "function"
        );

        if (!this._canRunAutomationMutation()) {
            return {
                didTranscribe: false,
                originalNode: this._serializeAutomationNode(selectedNode),
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeTranscribe,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }

        if (canTranscribe) {
            const languageCode = normalizedOptions.languageCode
                || (typeof this.model.getFirstLanguageCodeUsed === "function" ? this.model.getFirstLanguageCodeUsed() : null)
                || "en-US";
            await this.model.transcribeSequencerNode(selectedNode, languageCode);
        }

        const historyAfterTranscribe = this.getAutomationHistoryState();

        return {
            didTranscribe: canTranscribe
                && this._hasAutomationHistoryStateChanged(historyBeforeTranscribe, historyAfterTranscribe),
            originalNode: this._serializeAutomationNode(selectedNode),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterTranscribe,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    setAutomationSelectedMediaVolume: function(mainVolumePercentage) {
        const selectionInfo = this.view && typeof this.view.getSelectionNode === "function"
            ? this.view.getSelectionNode()
            : null;
        const selectedNode = selectionInfo && selectionInfo.startSequencerNodeModel === selectionInfo.endSequencerNodeModel
            ? selectionInfo.startSequencerNodeModel
            : null;
        const previousValue = selectedNode && typeof selectedNode.getMainVolumePercentage === "function"
            ? selectedNode.getMainVolumePercentage()
            : null;
        const historyBeforeChange = this.getAutomationHistoryState();
        const canUpdate = !!(
            selectedNode
            && typeof selectedNode.setMainVolumePercentage === "function"
            && typeof selectedNode.getMainVolumePercentage === "function"
            && this.model
            && typeof this.model.updateSequencerNodeProperty === "function"
        );

        if (!this._canRunAutomationMutation()) {
            return {
                didChange: false,
                previousValue,
                value: previousValue,
                node: this._serializeAutomationNode(selectedNode),
                historyChanged: false,
                history: historyBeforeChange,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }

        if (canUpdate && previousValue !== mainVolumePercentage) {
            this.model.updateSequencerNodeProperty(
                selectedNode,
                "setMainVolumePercentage",
                "getMainVolumePercentage",
                mainVolumePercentage
            );
        }

        const historyAfterChange = this.getAutomationHistoryState();
        const nextValue = selectedNode && typeof selectedNode.getMainVolumePercentage === "function"
            ? selectedNode.getMainVolumePercentage()
            : null;

        return {
            didChange: canUpdate && previousValue !== nextValue,
            previousValue,
            value: nextValue,
            node: this._serializeAutomationNode(selectedNode),
            historyChanged: this._hasAutomationHistoryStateChanged(historyBeforeChange, historyAfterChange),
            history: historyAfterChange,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    deleteAutomationSelectedSequencerNode: function() {
        const selectionInfo = this.view && typeof this.view.getSelectionNode === "function"
            ? this.view.getSelectionNode()
            : null;
        const selectedNode = selectionInfo && selectionInfo.startSequencerNodeModel === selectionInfo.endSequencerNodeModel
            ? selectionInfo.startSequencerNodeModel
            : null;
        const historyBeforeDelete = this.getAutomationHistoryState();

        if (!this._canRunAutomationMutation()) {
            return {
                didDelete: false,
                deletedNode: this._serializeAutomationNode(selectedNode),
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeDelete,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }

        if (selectedNode && this.model && typeof this.model.deleteSequencerNode === "function") {
            this.model.deleteSequencerNode(selectedNode);
        }

        const historyAfterDelete = this.getAutomationHistoryState();

        return {
            didDelete: !!selectedNode && this._hasAutomationHistoryStateChanged(historyBeforeDelete, historyAfterDelete),
            deletedNode: this._serializeAutomationNode(selectedNode),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterDelete,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    deleteAutomationSelection: function() {
        if (!this._canRunAutomationMutation()) {
            return {
                didDelete: false,
                deletedNode: null,
                clipboard: this.getAutomationClipboardState(),
                selection: this.getAutomationSelectionSnapshot(),
                history: this.getAutomationHistoryState(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const didDelete = !!this.onIgnoreActionTriggered();
        const deletedNode = this._lastAutomationDeletedNode || null;

        return {
            didDelete,
            deletedNode: deletedNode && typeof deletedNode === "object"
                ? this._serializeAutomationNode(deletedNode)
                : null,
            clipboard: this.getAutomationClipboardState(),
            selection: this.getAutomationSelectionSnapshot(),
            history: this.getAutomationHistoryState(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    restoreAutomationDeletedNode: function(nodeCid) {
        const deletedNode = this._getAutomationNodeByCid(nodeCid);
        const wasDeletedNode = !!(
            deletedNode
            && Utils.getInstance().isDeletedSequencerNodeInstance(deletedNode)
        );
        const restoredNodeSnapshot = wasDeletedNode
            ? this._serializeAutomationNode(deletedNode)
            : null;
        if (!this._canRunAutomationMutation()) {
            return {
                didRestore: false,
                restoredNode: restoredNodeSnapshot,
                clipboard: this.getAutomationClipboardState(),
                selection: this.getAutomationSelectionSnapshot(),
                history: this.getAutomationHistoryState(),
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        if(wasDeletedNode){
            this.model.revertDeletedNodeCreation(deletedNode);
            if(this.view && typeof this.view.unselectCurrentlySelectedSequencerNodes === "function"){
                this.view.unselectCurrentlySelectedSequencerNodes();
            }
        }

        return {
            didRestore: wasDeletedNode,
            restoredNode: restoredNodeSnapshot,
            clipboard: this.getAutomationClipboardState(),
            selection: this.getAutomationSelectionSnapshot(),
            history: this.getAutomationHistoryState(),
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    wrapAutomationSelectionWithMute: function() {
        const historyBeforeWrap = this.getAutomationHistoryState();
        const selectionInfo = this.view && typeof this.view.getSelectionNode === "function"
            ? this.view.getSelectionNode()
            : null;
        if (!this._canRunAutomationMutation()) {
            return {
                didWrap: false,
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeWrap,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        if(selectionInfo && selectionInfo.startSequencerNodeModel && selectionInfo.endSequencerNodeModel){
            this.model.wrapSelectedNodesWithMute(
                selectionInfo.startSequencerNodeModel,
                selectionInfo.endSequencerNodeModel
            );
        }
        const historyAfterWrap = this.getAutomationHistoryState();

        return {
            didWrap: this._hasAutomationHistoryStateChanged(historyBeforeWrap, historyAfterWrap),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterWrap,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    deleteAutomationMuteWrapNode: function(nodeCid) {
        const historyBeforeDelete = this.getAutomationHistoryState();
        const muteWrapNode = this._getAutomationNodeByCid(nodeCid);
        const isMuteWrapNode = !!(
            muteWrapNode
            && this._getAutomationNodeType(muteWrapNode)
            && this._getAutomationNodeType(muteWrapNode).indexOf("MuteWrapSequencerNode") !== -1
        );
        if (!this._canRunAutomationMutation()) {
            return {
                didDelete: false,
                deletedNode: this._serializeAutomationNode(muteWrapNode),
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeDelete,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        if(isMuteWrapNode){
            this.model.deleteMuteWrapNodeCreated(muteWrapNode);
        }
        const historyAfterDelete = this.getAutomationHistoryState();

        return {
            didDelete: isMuteWrapNode && this._hasAutomationHistoryStateChanged(historyBeforeDelete, historyAfterDelete),
            deletedNode: this._serializeAutomationNode(muteWrapNode),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterDelete,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    wrapAutomationSelectionWithMusic: function(options) {
        const historyBeforeWrap = this.getAutomationHistoryState();
        const selectionInfo = this.view && typeof this.view.getSelectionNode === "function"
            ? this.view.getSelectionNode()
            : null;
        if (!this._canRunAutomationMutation()) {
            return {
                didWrap: false,
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeWrap,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const mediaModel = this._createAutomationMediaModel("audio", options || {});
        if (
            mediaModel
            && selectionInfo
            && selectionInfo.startSequencerNodeModel
            && selectionInfo.endSequencerNodeModel
        ) {
            this.wrapSelectionWithMusic(mediaModel);
        }
        const historyAfterWrap = this.getAutomationHistoryState();

        return {
            didWrap: this._hasAutomationHistoryStateChanged(historyBeforeWrap, historyAfterWrap),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterWrap,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    stubAutomationNextMusicPickerSelection: function(options) {
        const router = PersistentModels.getInstance().getRouter();
        if (!router || typeof router.showChooseMusicSoundAndUploadsPopup !== "function") {
            return {
                didStub: false,
                reason: "missing-router-picker"
            };
        }

        const mediaModel = this._createAutomationMediaModel("audio", options || {});
        const originalShowPicker = router.showChooseMusicSoundAndUploadsPopup;
        let replacementShowPicker = null;
        const restorePicker = function() {
            if (router.showChooseMusicSoundAndUploadsPopup === replacementShowPicker) {
                router.showChooseMusicSoundAndUploadsPopup = originalShowPicker;
            }
        };

        replacementShowPicker = function(onTrebbleAudioSelected) {
            restorePicker();
            if (typeof onTrebbleAudioSelected === "function") {
                onTrebbleAudioSelected(mediaModel);
            }
            return RSVP.Promise.resolve({
                automationPickerSelection: true
            });
        };

        router.showChooseMusicSoundAndUploadsPopup = replacementShowPicker;

        return {
            didStub: true,
            mediaId: mediaModel && typeof mediaModel.get === "function" ? mediaModel.get("id") : null,
            title: mediaModel && typeof mediaModel.get === "function" ? mediaModel.get("title") : null
        };
    },

    stubAutomationNextRecordAndInsertUpload: async function(mediaItems) {
        const normalizedItems = Array.isArray(mediaItems) ? mediaItems : [];
        if (!normalizedItems.length) {
            return {
                didStub: false,
                reason: "missing-media-items"
            };
        }

        const router = PersistentModels.getInstance().getRouter();
        const fileUploadHelper = FileUploadHelper.getInstance();
        if (
            !router
            || typeof router.executeFunctionAndShowProgressBarPopup !== "function"
            || !fileUploadHelper
            || typeof fileUploadHelper.showUploadDialogAndUploadFileToSoundLibrary !== "function"
        ) {
            return {
                didStub: false,
                reason: "missing-record-upload-dependencies"
            };
        }

        const recordedFiles = await Promise.all(normalizedItems.map(async (mediaItem, index) => {
            const normalizedMediaItem = mediaItem || {};
            const mediaKind = normalizedMediaItem.mediaKind === "video" ? "video" : "audio";
            const mediaUrl = normalizedMediaItem.audioUrl || normalizedMediaItem.mediaUrl;
            if (!mediaUrl) {
                throw new Error("Cannot stub record-and-insert upload without an audioUrl/mediaUrl.");
            }
            const response = await fetch(mediaUrl);
            const blob = await response.blob();
            const mimeType = normalizedMediaItem.mimeType || blob.type || (mediaKind === "video" ? "video/webm" : "audio/wav");
            const extension = mediaKind === "video" ? "webm" : "wav";
            return new File(
                [blob],
                normalizedMediaItem.fileName || `automation-recording-${index + 1}.${extension}`,
                { type: mimeType }
            );
        }));
        const uploadResults = normalizedItems.map((mediaItem) => {
            const normalizedMediaItem = mediaItem || {};
            const mediaKind = normalizedMediaItem.mediaKind === "video" ? "video" : "audio";
            const mediaModel = this._createAutomationMediaModel(mediaKind, normalizedMediaItem);
            return mediaModel && typeof mediaModel.toJSON === "function"
                ? mediaModel.toJSON()
                : Object.assign({}, mediaModel && mediaModel.attributes ? mediaModel.attributes : {});
        });

        const controller = this;
        const originalGetUserToRecord = controller.getUserToRecord;
        const originalExecuteFunctionAndShowProgressBarPopup = router.executeFunctionAndShowProgressBarPopup;
        const originalShowUploadDialogAndUploadFileToSoundLibrary = fileUploadHelper.showUploadDialogAndUploadFileToSoundLibrary;
        let uploadIndex = 0;
        let restoreStubs = function() {};
        const getUserToRecordStub = async function(onRecordingsReady) {
            controller.getUserToRecord = originalGetUserToRecord;
            if(onRecordingsReady){
                await onRecordingsReady(recordedFiles);
            }
            return recordedFiles;
        };
        const executeFunctionAndShowProgressBarPopupStub = function(actionToExecute) {
            return actionToExecute(function() {});
        };
        const showUploadDialogAndUploadFileToSoundLibraryStub = function() {
            const uploadResult = uploadResults[uploadIndex] || null;
            uploadIndex = uploadIndex + 1;
            if (uploadIndex >= uploadResults.length) {
                restoreStubs();
            }
            return RSVP.Promise.resolve(uploadResult);
        };
        restoreStubs = function() {
            if (controller.getUserToRecord === getUserToRecordStub) {
                controller.getUserToRecord = originalGetUserToRecord;
            }
            if (router.executeFunctionAndShowProgressBarPopup === executeFunctionAndShowProgressBarPopupStub) {
                router.executeFunctionAndShowProgressBarPopup = originalExecuteFunctionAndShowProgressBarPopup;
            }
            if (fileUploadHelper.showUploadDialogAndUploadFileToSoundLibrary === showUploadDialogAndUploadFileToSoundLibraryStub) {
                fileUploadHelper.showUploadDialogAndUploadFileToSoundLibrary = originalShowUploadDialogAndUploadFileToSoundLibrary;
            }
        };

        controller.getUserToRecord = getUserToRecordStub;
        router.executeFunctionAndShowProgressBarPopup = executeFunctionAndShowProgressBarPopupStub;
        fileUploadHelper.showUploadDialogAndUploadFileToSoundLibrary = showUploadDialogAndUploadFileToSoundLibraryStub;

        return {
            didStub: true,
            mediaCount: normalizedItems.length,
            fileNames: recordedFiles.map((file) => file.name)
        };
    },

    deleteAutomationMusicWrapNode: function(nodeCid) {
        const historyBeforeDelete = this.getAutomationHistoryState();
        const musicWrapNode = this._getAutomationNodeByCid(nodeCid);
        const nodeType = musicWrapNode && this._getAutomationNodeType(musicWrapNode);
        const isMusicWrapNode = !!(
            musicWrapNode
            && nodeType
            && nodeType.indexOf("MusicWrapSequencerNode") !== -1
        );
        if (!this._canRunAutomationMutation()) {
            return {
                didDelete: false,
                deletedNode: this._serializeAutomationNode(musicWrapNode),
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeDelete,
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        if (isMusicWrapNode) {
            this.model.deleteMusicWrapNodeCreated(musicWrapNode);
        }
        const historyAfterDelete = this.getAutomationHistoryState();

        return {
            didDelete: isMusicWrapNode && this._hasAutomationHistoryStateChanged(historyBeforeDelete, historyAfterDelete),
            deletedNode: this._serializeAutomationNode(musicWrapNode),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterDelete,
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    copyAutomationSelection: function() {
        const canCopySelection = this._canAutomationCopyCurrentSelection();
        this.copySelectionToClipboard();
        const clipboardAfterCopy = this.getAutomationClipboardState();

        return {
            didCopy: canCopySelection && !clipboardAfterCopy.isEmpty,
            clipboard: clipboardAfterCopy,
            selection: this.getAutomationSelectionSnapshot(),
            history: this.getAutomationHistoryState(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    cutAutomationSelection: function() {
        const historyBeforeCut = this.getAutomationHistoryState();
        const transcriptBeforeCut = this.getAutomationTranscriptSnapshot();

        if (!this._canRunAutomationMutation()) {
            return {
                didCut: false,
                clipboard: this.getAutomationClipboardState(),
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforeCut,
                transcript: transcriptBeforeCut
            };
        }
        this.cutAndCopySelectionToClipboard();

        const historyAfterCut = this.getAutomationHistoryState();
        return {
            didCut: this._hasAutomationHistoryStateChanged(historyBeforeCut, historyAfterCut),
            clipboard: this.getAutomationClipboardState(),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterCut,
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    setAutomationNoiseCancellation: function(on) {
        const targetValue = !!on;
        const settingsBeforeChange = this.getAutomationSequencerSettingsSnapshot();
        const historyBeforeChange = this.getAutomationHistoryState();

        if (!this._canRunAutomationMutation()) {
            return {
                didChange: false,
                previousValue: settingsBeforeChange.noiseCancellationOn,
                value: settingsBeforeChange.noiseCancellationOn,
                historyChanged: false,
                settings: settingsBeforeChange,
                history: historyBeforeChange,
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }

        if (settingsBeforeChange.noiseCancellationOn !== targetValue) {
            this.setNoiseCancellation(targetValue, false);
        }

        const historyAfterChange = this.getAutomationHistoryState();
        const settingsAfterChange = this.getAutomationSequencerSettingsSnapshot();

        return {
            didChange: settingsBeforeChange.noiseCancellationOn !== settingsAfterChange.noiseCancellationOn,
            previousValue: settingsBeforeChange.noiseCancellationOn,
            value: settingsAfterChange.noiseCancellationOn,
            historyChanged: this._hasAutomationHistoryStateChanged(historyBeforeChange, historyAfterChange),
            settings: settingsAfterChange,
            history: historyAfterChange,
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    setAutomationSequencerSetting: function(settingKeyOrName, value) {
        const settingsBeforeChange = this.getAutomationSequencerSettingsSnapshot();
        const changeableSettings = settingsBeforeChange.changeableSettings || {};
        const settingName = changeableSettings[settingKeyOrName] || settingKeyOrName;
        const settingKey = Object.keys(changeableSettings).find((candidateKey) => {
            return candidateKey === settingKeyOrName || changeableSettings[candidateKey] === settingName;
        }) || settingName;
        const previousValue = settingsBeforeChange.settingValues
            ? settingsBeforeChange.settingValues[settingKey]
            : undefined;
        const historyBeforeChange = this.getAutomationHistoryState();

        if (!this._canRunAutomationMutation()) {
            return {
                didChange: false,
                settingKey,
                settingName,
                previousValue,
                value: previousValue,
                historyChanged: false,
                settings: settingsBeforeChange,
                history: historyBeforeChange,
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }

        if (this.model && typeof this.model.setSequencerSettings === "function" && previousValue !== value) {
            this.model.setSequencerSettings(settingName, value, false);
        }

        const historyAfterChange = this.getAutomationHistoryState();
        const settingsAfterChange = this.getAutomationSequencerSettingsSnapshot();
        const nextValue = settingsAfterChange.settingValues
            ? settingsAfterChange.settingValues[settingKey]
            : undefined;

        return {
            didChange: previousValue !== nextValue,
            settingKey,
            settingName,
            previousValue,
            value: nextValue,
            historyChanged: this._hasAutomationHistoryStateChanged(historyBeforeChange, historyAfterChange),
            settings: settingsAfterChange,
            history: historyAfterChange,
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    pasteAutomationClipboardBeforeSelection: function() {
        const transcriptBeforePaste = this.getAutomationTranscriptSnapshot();
        const historyBeforePaste = this.getAutomationHistoryState();

        if (!this._canRunAutomationMutation()) {
            return {
                didPaste: false,
                clipboard: this.getAutomationClipboardState(),
                selection: this.getAutomationSelectionSnapshot(),
                history: historyBeforePaste,
                transcript: transcriptBeforePaste
            };
        }

        if (this.model && typeof this.model.isClipboardEmpty === "function" && this.model.isClipboardEmpty()) {
            return {
                didPaste: false,
                clipboard: this.getAutomationClipboardState(),
                selection: this.getAutomationSelectionSnapshot(),
                history: this.getAutomationHistoryState(),
                transcript: transcriptBeforePaste
            };
        }

        this.pasteClipboardBeforeSelection();

        const historyAfterPaste = this.getAutomationHistoryState();
        const transcriptAfterPaste = this.getAutomationTranscriptSnapshot();
        return {
            didPaste: this._hasAutomationHistoryStateChanged(historyBeforePaste, historyAfterPaste),
            clipboard: this.getAutomationClipboardState(),
            selection: this.getAutomationSelectionSnapshot(),
            history: historyAfterPaste,
            transcript: transcriptAfterPaste
        };
    },

    undoAutomation: function() {
        const historyBeforeUndo = this.getAutomationHistoryState();
        if (!this._canRunAutomationMutation()) {
            return {
                didUndo: false,
                history: historyBeforeUndo,
                selection: this.getAutomationSelectionSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        if (!historyBeforeUndo.canUndo) {
            return {
                didUndo: false,
                history: historyBeforeUndo,
                selection: this.getAutomationSelectionSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }

        this.onUndoOperationTriggered();

        return {
            didUndo: true,
            history: this.getAutomationHistoryState(),
            selection: this.getAutomationSelectionSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    redoAutomation: function() {
        const historyBeforeRedo = this.getAutomationHistoryState();
        if (!this._canRunAutomationMutation()) {
            return {
                didRedo: false,
                history: historyBeforeRedo,
                selection: this.getAutomationSelectionSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        if (!historyBeforeRedo.canRedo) {
            return {
                didRedo: false,
                history: historyBeforeRedo,
                selection: this.getAutomationSelectionSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }

        this.onRedoOperationTriggered();

        return {
            didRedo: true,
            history: this.getAutomationHistoryState(),
            selection: this.getAutomationSelectionSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    saveAutomationProject: async function() {
        await this.saveProject();

        return {
            projectId: this.getProjectId ? this.getProjectId() : null,
            history: this.getAutomationHistoryState(),
            selection: this.getAutomationSelectionSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    applyAutomationParagraphBoundaryAction: function(actionName) {
        if (!this._canRunAutomationMutation()) {
            return {
                didApply: false,
                actionName,
                history: this.getAutomationHistoryState(),
                selection: this.getAutomationSelectionSnapshot(),
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const didApply = !!(
            this.view
            && typeof this.view.applyAutomationParagraphBoundaryAction === "function"
            && this.view.applyAutomationParagraphBoundaryAction(actionName)
        );

        return {
            didApply,
            actionName,
            history: this.getAutomationHistoryState(),
            selection: this.getAutomationSelectionSnapshot(),
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    assignAutomationSpeakerToSelection: function(options) {
        if (!this._canRunAutomationMutation()) {
            return {
                didChange: false,
                history: this.getAutomationHistoryState(),
                selection: this.getAutomationSelectionSnapshot(),
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const result = this.view && typeof this.view.assignAutomationSpeakerToSelection === "function"
            ? this.view.assignAutomationSpeakerToSelection(options || {})
            : { didChange: false };

        return {
            ...result,
            history: this.getAutomationHistoryState(),
            selection: this.getAutomationSelectionSnapshot(),
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    correctAutomationTextOnSelection: function(options) {
        if (!this._canRunAutomationMutation()) {
            return {
                didChange: false,
                history: this.getAutomationHistoryState(),
                selection: this.getAutomationSelectionSnapshot(),
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const result = this.view && typeof this.view.correctAutomationTextOnSelection === "function"
            ? this.view.correctAutomationTextOnSelection(options || {})
            : { didChange: false };

        return {
            ...result,
            history: this.getAutomationHistoryState(),
            selection: this.getAutomationSelectionSnapshot(),
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    renameAutomationSelectedSpeaker: function(options) {
        if (!this._canRunAutomationMutation()) {
            return {
                didChange: false,
                history: this.getAutomationHistoryState(),
                selection: this.getAutomationSelectionSnapshot(),
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const result = this.view && typeof this.view.renameAutomationSelectedSpeaker === "function"
            ? this.view.renameAutomationSelectedSpeaker(options || {})
            : { didChange: false };

        return {
            ...result,
            history: this.getAutomationHistoryState(),
            selection: this.getAutomationSelectionSnapshot(),
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    createAutomationNewSpeakerAndAssignToSelection: function(options) {
        if (!this._canRunAutomationMutation()) {
            return {
                didChange: false,
                history: this.getAutomationHistoryState(),
                selection: this.getAutomationSelectionSnapshot(),
                paragraph: this.getAutomationParagraphSnapshot(),
                transcript: this.getAutomationTranscriptSnapshot()
            };
        }
        const result = this.view && typeof this.view.createAutomationNewSpeakerAndAssignToSelection === "function"
            ? this.view.createAutomationNewSpeakerAndAssignToSelection(options || {})
            : { didChange: false };

        return {
            ...result,
            history: this.getAutomationHistoryState(),
            selection: this.getAutomationSelectionSnapshot(),
            paragraph: this.getAutomationParagraphSnapshot(),
            transcript: this.getAutomationTranscriptSnapshot()
        };
    },

    selectAutomationText: function(queryText, options) {
        return this.view && typeof this.view.selectAutomationText === "function"
            ? this.view.selectAutomationText(queryText, options || {})
            : null;
    },

    selectAutomationVisibleWordRange: function(startWordIndex, endWordIndex, options) {
        return this.view && typeof this.view.selectAutomationVisibleWordRange === "function"
            ? this.view.selectAutomationVisibleWordRange(
                startWordIndex,
                endWordIndex,
                options ? options.doNotClearBrowserSelectionToNotLooseFocus : undefined
            )
            : null;
    },

    selectRandomAutomationVisibleWordRange: function(options) {
        return this.view && typeof this.view.selectRandomAutomationVisibleWordRange === "function"
            ? this.view.selectRandomAutomationVisibleWordRange(options || {})
            : null;
    },

    selectAutomationNode: function(nodeCid, options) {
        const sequencerNode = this._getAutomationNodeByCid(nodeCid);
        if (!sequencerNode || !this.view || typeof this.view.selectSequencerNode !== "function") {
            return null;
        }

        this.view.selectSequencerNode(
            sequencerNode,
            options ? options.doNotClearBrowserSelectionToNotLooseFocus : undefined
        );

        return {
            node: this._serializeAutomationNode(sequencerNode),
            selection: this.getAutomationSelectionSnapshot()
        };
    },

    _createAutomationApi: function() {
        const automationApi = {
            getMetadata: this.getAutomationMetadata.bind(this),
            getTranscriptSnapshot: this.getAutomationTranscriptSnapshot.bind(this),
            getSelection: this.getAutomationSelectionSnapshot.bind(this),
            getHistoryState: this.getAutomationHistoryState.bind(this),
            getSequencerSettingsSnapshot: this.getAutomationSequencerSettingsSnapshot.bind(this),
            getClipboardState: this.getAutomationClipboardState.bind(this),
            getRandomVisibleWordRange: this.getRandomAutomationVisibleWordRange.bind(this),
            findText: this.findAutomationText.bind(this),
            getNode: this.getAutomationNodeSnapshot.bind(this),
            getNodeSnapshots: this.getAutomationNodeSnapshots.bind(this),
            getRenderedAudioMetrics: this.getAutomationRenderedAudioMetrics.bind(this),
            getPlaybackDebugState: this.getAutomationPlaybackDebugState.bind(this),
            getVideoPreviewState: this.getAutomationVideoPreviewState.bind(this),
            previewNode: this.previewAutomationNode.bind(this),
            selectText: this.selectAutomationText.bind(this),
            selectVisibleWordRange: this.selectAutomationVisibleWordRange.bind(this),
            selectRandomVisibleWordRange: this.selectRandomAutomationVisibleWordRange.bind(this),
            selectNode: this.selectAutomationNode.bind(this),
            getParagraphSnapshot: this.getAutomationParagraphSnapshot.bind(this),
            insertSyntheticMediaBeforeSelection: this.insertAutomationSyntheticMediaBeforeSelection.bind(this),
            insertSyntheticMediaBulkBeforeSelection: this.insertAutomationSyntheticMediaBulkBeforeSelection.bind(this),
            transcribeSelectedMedia: this.transcribeAutomationSelectedMedia.bind(this),
            setSelectedMediaVolume: this.setAutomationSelectedMediaVolume.bind(this),
            deleteSelectedSequencerNode: this.deleteAutomationSelectedSequencerNode.bind(this),
            deleteSelection: this.deleteAutomationSelection.bind(this),
            restoreDeletedNode: this.restoreAutomationDeletedNode.bind(this),
            wrapSelectionWithMute: this.wrapAutomationSelectionWithMute.bind(this),
            deleteMuteWrapNode: this.deleteAutomationMuteWrapNode.bind(this),
            wrapSelectionWithMusic: this.wrapAutomationSelectionWithMusic.bind(this),
            stubNextMusicPickerSelection: this.stubAutomationNextMusicPickerSelection.bind(this),
            stubNextRecordAndInsertUpload: this.stubAutomationNextRecordAndInsertUpload.bind(this),
            deleteMusicWrapNode: this.deleteAutomationMusicWrapNode.bind(this),
            moveNode: this.moveAutomationNode.bind(this),
            moveWrapNode: this.moveAutomationWrapNode.bind(this),
            copySelection: this.copyAutomationSelection.bind(this),
            cutSelection: this.cutAutomationSelection.bind(this),
            pasteClipboardBeforeSelection: this.pasteAutomationClipboardBeforeSelection.bind(this),
            undo: this.undoAutomation.bind(this),
            redo: this.redoAutomation.bind(this),
            saveProject: this.saveAutomationProject.bind(this),
            applyParagraphBoundaryAction: this.applyAutomationParagraphBoundaryAction.bind(this),
            correctTextOnSelection: this.correctAutomationTextOnSelection.bind(this),
            assignSpeakerToSelection: this.assignAutomationSpeakerToSelection.bind(this),
            renameSelectedSpeaker: this.renameAutomationSelectedSpeaker.bind(this),
            createNewSpeakerAndAssignToSelection: this.createAutomationNewSpeakerAndAssignToSelection.bind(this),
            setNoiseCancellation: this.setAutomationNoiseCancellation.bind(this),
            setSequencerSetting: this.setAutomationSequencerSetting.bind(this),
            seekVideoPreviewToTimeMs: this.seekVideoPreviewToTimeMsForAutomation.bind(this)
        };

        if (areEditorWriteToolsEnabled()) {
            return automationApi;
        }

        // TRE-1205: on production builds, expose only the read methods. Gating the web agent
        // bridge alone removed the advertised tool menu but not the capability — this object
        // is the capability, reachable from any console or injected script as
        // window.trebble.globalHelpers.audioEditorAutomation.getActiveEditor().
        // Strip rather than no-op the mutating methods so callers get a clear
        // "command is unavailable" instead of a silent success that edited nothing.
        const readOnlyAutomationApi = {};
        AUTOMATION_READ_ONLY_METHOD_NAMES.forEach(function(methodName) {
            readOnlyAutomationApi[methodName] = automationApi[methodName];
        });
        return readOnlyAutomationApi;
    },

    _registerAutomationApi: function() {
        const registry = ensureAudioEditorAutomationRegistry();
        if (!registry) {
            return;
        }

        const editorId = this.getAutomationEditorId();
        if (!editorId) {
            return;
        }

        registry._editorsById[editorId] = this._createAutomationApi();
        registry._activeEditorId = editorId;
        registerAudioEditorWebAgentBridge();
    },

    _unregisterAutomationApi: function() {
        const registry = ensureAudioEditorAutomationRegistry();
        const editorId = this.getAutomationEditorId();

        if (!registry || !editorId) {
            return;
        }

        delete registry._editorsById[editorId];
        if (registry._activeEditorId === editorId) {
            const remainingEditorIds = Object.keys(registry._editorsById);
            registry._activeEditorId = remainingEditorIds.length > 0
                ? remainingEditorIds[remainingEditorIds.length - 1]
                : null;
        }
        releaseAudioEditorWebAgentBridge();
    },

    setJqueryPageElWhereSequencerWillBeAdded : function(jqueryEl){
        this.view.setJqueryPageElWhereSequencerWillBeAdded(jqueryEl);
    },

    addTranscribeAudio : function(audioUrl, transcriptionJsonURL, transcriptionId, doNotAddToSequencer, isVideo, progressReportFunction, languageCode, width, height){

        if(this.view){
            this.view.setTranscribedAudioBeingAdded(true);
        }else{
            this._setTranscribedAudioBeingAddedWhenViewGetCreated = true;
        }
        return this.model.addTranscribeAudio(audioUrl, transcriptionJsonURL, transcriptionId, doNotAddToSequencer, isVideo, progressReportFunction, null, null, languageCode, width, height).then((function(){
            if(this._setTranscribedAudioBeingAddedWhenViewGetCreated){
                this._setTranscribedAudioBeingAddedWhenViewGetCreated = false;
            }
            if(this.view){
                this.view.setTranscribedAudioBeingAdded(false);
            }
            this.showOnboardingIfApplicable();
        }).bind(this));
    },

    onIgnoreActionTriggered : function(){
        if(typeof this.hasMutationAccess === "function" && !this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view && typeof this.view.getSelectionNode === "function" ? this.view.getSelectionNode() : null;
        this._lastAutomationDeletedNode = null;
        if(selectionInfo){
            const startSequencerNodeModel = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeModel = selectionInfo.endSequencerNodeModel;
            if(!this.model || typeof this.model.deleteSelectedNodes !== "function" || !startSequencerNodeModel || !endSequencerNodeModel || typeof startSequencerNodeModel.getNext !== "function" || typeof endSequencerNodeModel.getNext !== "function"){
                if(this.view && typeof this.view.unselectCurrentlySelectedSequencerNodes === "function"){
                    this.view.unselectCurrentlySelectedSequencerNodes();
                }
                return false;
            }

            // Check if it's a single wrap node that needs special handling
            if(startSequencerNodeModel == endSequencerNodeModel){
                const deletionMethod = Utils.getInstance().getWrapNodeDeletionMethod(startSequencerNodeModel);
                if(deletionMethod && typeof this.model[deletionMethod] === 'function'){
                    // It's a wrap node - call the appropriate deletion method
                    this.model[deletionMethod](startSequencerNodeModel);
                    this.view.unselectCurrentlySelectedSequencerNodes();
                    return true;
                }
            }

            // For multi-node selection, validate it doesn't contain orphan wrap nodes
            // The deleteSelectedNodes method will catch this, but we could add
            // more helpful error messaging here if needed in the future

            // Keep the caret anchored to the next still-live visible node when possible.
            // This restores the pre-April feel of "delete in place" while also avoiding
            // the deleted-placeholder auto-select path, which forces a paragraph rebuild
            // flush before the interaction can finish.
            let preferredNodeToSelectAfterDelete = null;
            if(endSequencerNodeModel && typeof endSequencerNodeModel.getNextVisibleSequencerNode === "function"){
                preferredNodeToSelectAfterDelete = endSequencerNodeModel.getNextVisibleSequencerNode();
            }
            if(!preferredNodeToSelectAfterDelete && startSequencerNodeModel && typeof startSequencerNodeModel.getPreviousVisibleSequencerNode === "function"){
                preferredNodeToSelectAfterDelete = startSequencerNodeModel.getPreviousVisibleSequencerNode();
            }

            // For non-wrap nodes or multi-node selection, use the standard deletion
            const deletedNode = this.model.deleteSelectedNodes(startSequencerNodeModel, endSequencerNodeModel);
            if(deletedNode){
                this._lastAutomationDeletedNode = deletedNode;
                if(
                    preferredNodeToSelectAfterDelete
                    && this.view
                    && typeof this.view.selectAttachedSequencerNodeWithoutParagraphFlush === "function"
                    && this.view.selectAttachedSequencerNodeWithoutParagraphFlush(preferredNodeToSelectAfterDelete, true)
                ){
                    if(
                        typeof this.view.getSequencerNodeViewFromSequencerNode === "function"
                        && typeof this.view.scrollToSequencerNodeIfneccessary === "function"
                    ){
                        const nodeViewToKeepSelected = this.view.getSequencerNodeViewFromSequencerNode(preferredNodeToSelectAfterDelete);
                        if(nodeViewToKeepSelected){
                            this.view.scrollToSequencerNodeIfneccessary(nodeViewToKeepSelected, false, true, true);
                        }
                    }
                    return true;
                }
                this.view.unselectCurrentlySelectedSequencerNodes();
                return true;
            }else{
                this.view.unselectCurrentlySelectedSequencerNodes();
                return false;
            }
        }

        return false;
    },

    setAndLoadProject: function(projectId, duplicateProject, progressReportFunction){
        return this.model.setAndLoadProject(projectId, duplicateProject, progressReportFunction);
    },

    getRenderedAudioBlob : function(renderWavFile, asVideo, progressReportFunction, alreadyRenderedAudioWAVBlob){
        return this.model.getRenderedAudioBlob(renderWavFile, asVideo, progressReportFunction, alreadyRenderedAudioWAVBlob);
    },

    createFFMPEGParamsToRenderVideoByContatenation : function(audioUrl, srtFileUrl){
        return this.model.createFFMPEGParamsToRenderVideoByContatenation(true, audioUrl, null, null, srtFileUrl);
    },

    createFFMPEGParamsByContatenationForServerRendering : function(sequencerNodeToStartFrom, sequencerNodeToEndOn, audioUrl , maxDurationInMilliseconds ){
        return this.model.createFFMPEGParamsByContatenationForServerRendering(true, sequencerNodeToStartFrom, sequencerNodeToEndOn, audioUrl, maxDurationInMilliseconds);
    },

    getSrtText : function(sequencerNodeToStartFrom, sequencerNodeToEndOn, progressReportFunction, asVtt, doNotIncludeSpeaker){
        return this.model.getSrtText(sequencerNodeToStartFrom, sequencerNodeToEndOn, progressReportFunction, asVtt, doNotIncludeSpeaker);
    },

    /**
     * Export timeline as EDL (Edit Decision List) format
     */
    exportEDL: function(startNode, endNode, options) {
        return this.model.exportEDL(startNode, endNode, options);
    },

    /**
     * Export timeline as FCPXML (Final Cut Pro XML) format
     */
    exportFCPXML: function(startNode, endNode, options) {
        return this.model.exportFCPXML(startNode, endNode, options);
    },

    /**
     * Download EDL export as file
     */
    downloadEDL: function(startNode, endNode, options) {
        return this.model.downloadEDL(startNode, endNode, options);
    },

    /**
     * Download FCPXML export as file
     */
    downloadFCPXML: function(startNode, endNode, options) {
        return this.model.downloadFCPXML(startNode, endNode, options);
    },

    /**
     * Export current selection as EDL
     */
    exportSelectionAsEDL: function(options) {
        const selectionInfo = this.model.getCurrentSelectionInfo();
        if (selectionInfo && selectionInfo.startSequencerNodeModel && selectionInfo.endSequencerNodeModel) {
            return this.model.exportEDL(
                selectionInfo.startSequencerNodeModel,
                selectionInfo.endSequencerNodeModel,
                options
            );
        } else {
            return this.model.exportEDL(null, null, options);
        }
    },

    /**
     * Export current selection as FCPXML
     */
    exportSelectionAsFCPXML: function(options) {
        const selectionInfo = this.model.getCurrentSelectionInfo();
        if (selectionInfo && selectionInfo.startSequencerNodeModel && selectionInfo.endSequencerNodeModel) {
            return this.model.exportFCPXML(
                selectionInfo.startSequencerNodeModel,
                selectionInfo.endSequencerNodeModel,
                options
            );
        } else {
            return this.model.exportFCPXML(null, null, options);
        }
    },

    /**
     * Download current selection as EDL file
     */
    downloadSelectionAsEDL: function(options) {
        const selectionInfo = this.model.getCurrentSelectionInfo();
        if (selectionInfo && selectionInfo.startSequencerNodeModel && selectionInfo.endSequencerNodeModel) {
            return this.model.downloadEDL(
                selectionInfo.startSequencerNodeModel,
                selectionInfo.endSequencerNodeModel,
                options
            );
        } else {
            return this.model.downloadEDL(null, null, options);
        }
    },

    /**
     * Download current selection as FCPXML file
     */
    downloadSelectionAsFCPXML: function(options) {
        const selectionInfo = this.model.getCurrentSelectionInfo();
        if (selectionInfo && selectionInfo.startSequencerNodeModel && selectionInfo.endSequencerNodeModel) {
            return this.model.downloadFCPXML(
                selectionInfo.startSequencerNodeModel,
                selectionInfo.endSequencerNodeModel,
                options
            );
        } else {
            return this.model.downloadFCPXML(null, null, options);
        }
    },

    onWrapWithMusicActionTriggered : function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const onBackgroundMusicSelected = this.wrapSelectionWithMusic.bind(this);
        return PersistentModels.getInstance().getRouter().showChooseMusicSoundAndUploadsPopup(onBackgroundMusicSelected);
    },

    onWrapWithMuteActionTriggered : function(){
        if(!this._canRunAutomationMutation()){
            return null;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo && selectionInfo.startSequencerNodeModel && selectionInfo.endSequencerNodeModel){
            const startSequencerNodeToWrap = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeToWrap = selectionInfo.endSequencerNodeModel;
            this.model.wrapSelectedNodesWithMute(startSequencerNodeToWrap, endSequencerNodeToWrap);
        }
    },

    getDurationInMilliseconds : function(){
        return this.model.getDurationInMilliseconds();
    },

    wrapSelectionWithMusic : function(trebbleAudioModel){
        if(!this.hasMutationAccess()){
            return;
        }
        if(trebbleAudioModel){
            const selectionInfo = this.view.getSelectionNode();
            if(selectionInfo){
                const startSequencerNodeToWrap = selectionInfo.startSequencerNodeModel;
                const endSequencerNodeToWrap = selectionInfo.endSequencerNodeModel;
                const introFadeInMilliseconds = 0;
                const outroFadeinMilliseconds = 0;
                const introPaddingInMilliseconds = 0;
                const outroPaddingInMilliseconds = 0;
                const loopToMatchWrapNodeDuration = false;
                const ducking = false;
                const volume = (ALLOW_DUCKING_OF_BACKGROUND_MUSIC_IN_AUDIO_EDITOR && ducking) ? 0.50 : 0.20;
                const wrapNodes = this.model.wrapSelectedNodesWithMusic(trebbleAudioModel, introPaddingInMilliseconds, outroPaddingInMilliseconds, introFadeInMilliseconds,outroFadeinMilliseconds, volume, loopToMatchWrapNodeDuration, ducking,  startSequencerNodeToWrap, endSequencerNodeToWrap);

                // Auto-select the created node so user can immediately configure in inspector
                if (wrapNodes && wrapNodes.startWrapSequencerNode) {
                    this._selectCreatedNodeIfEnabled(wrapNodes.startWrapSequencerNode, true);
                }
            }
        }
    },

    deleteAllExceptSelectedNodes: function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const startSequencerNodeToKeep = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeToKeep = selectionInfo.endSequencerNodeModel;
            this.model.deleteAllExceptSelectedNodes(startSequencerNodeToKeep, endSequencerNodeToKeep);
            this.view.unselectCurrentlySelectedSequencerNodes();
        }
    },

    deleteAllExceptSelectedNodesAndSaveThenUndo: async function(startSequencerNodeToKeep, endSequencerNodeToKeep, operationNameWhichTriggerHistoryUpdate){
        if(!this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        startSequencerNodeToKeep = startSequencerNodeToKeep? startSequencerNodeToKeep:selectionInfo? selectionInfo.startSequencerNodeModel:null;
        endSequencerNodeToKeep = endSequencerNodeToKeep? endSequencerNodeToKeep:selectionInfo? selectionInfo.endSequencerNodeModel:null;
        if(startSequencerNodeToKeep && endSequencerNodeToKeep){
            this.model.deleteAllExceptSelectedNodes(startSequencerNodeToKeep, endSequencerNodeToKeep);
            await this.model.saveProject(true, operationNameWhichTriggerHistoryUpdate);
            const projectIdToReturn = this.model.getProjectId();
            this.model.getHistoryManagement().undo(true);
            return projectIdToReturn;
        }

    },


    onInsertAudioNodeTriggered : function(insertBefore){
        if(!this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const startSequencerNodeModel = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeModel = selectionInfo.endSequencerNodeModel;
            if(startSequencerNodeModel || endSequencerNodeModel){
                //One node selected
                const onTrebbleAudioSelected = (function(trebbleAudio){
                    this.insertAudioMode(insertBefore? startSequencerNodeModel: endSequencerNodeModel , insertBefore, trebbleAudio);
                }).bind(this);
                return PersistentModels.getInstance().getRouter().showChooseMusicSoundAndUploadsPopup(onTrebbleAudioSelected, false, false);
            }
        }

    },

    onInsertVideoNodeTriggered: function(insertBefore){
        if(!this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const startSequencerNodeModel = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeModel = selectionInfo.endSequencerNodeModel;
            if(startSequencerNodeModel || endSequencerNodeModel){
                //One node selected
                const onTrebbleVideoSelected = (function(trebbleVideo){
                    this.insertVideoMode(insertBefore? startSequencerNodeModel: endSequencerNodeModel , insertBefore, trebbleVideo);
                }).bind(this);
                return PersistentModels.getInstance().getRouter().showChooseUploadedVideosPopup(onTrebbleVideoSelected);
            }
        }

    },

    insertAudioMode : function(sequencerNodeModelTarget, insertBefore, trebbleAudio){
        if(!this.hasMutationAccess()){
            return;
        }
        if(sequencerNodeModelTarget && trebbleAudio){
            //One node selected
            const introFadeInMilliseconds = 0;
            const outroFadeinMilliseconds = 0;
            const introPaddingInMilliseconds = 0;
            const outroPaddingInMilliseconds = 0;
            const volume = 0.5;
            const createdNode = this.model.insertAudioNode(trebbleAudio, sequencerNodeModelTarget, insertBefore, introPaddingInMilliseconds, outroPaddingInMilliseconds,introFadeInMilliseconds,outroFadeinMilliseconds, volume);

            // Auto-select the created node so user can immediately configure in inspector
            if (createdNode) {
                this._selectCreatedNodeIfEnabled(createdNode, true);
            }
            return createdNode;
        }

    },

    insertVideoMode : function(sequencerNodeModelTarget, insertBefore, trebbleVideo){
        if(!this.hasMutationAccess()){
            return;
        }
        if(sequencerNodeModelTarget && trebbleVideo){
            //One node selected
            const introFadeInMilliseconds = 0;
            const outroFadeinMilliseconds = 0;
            const introPaddingInMilliseconds = 0;
            const outroPaddingInMilliseconds = 0;
            const volume = 0.5;
            const createdNode = this.model.insertVideoNode(trebbleVideo, sequencerNodeModelTarget, insertBefore, introPaddingInMilliseconds, outroPaddingInMilliseconds,introFadeInMilliseconds,outroFadeinMilliseconds, volume);

            // Auto-select the created node so user can immediately configure in inspector
            if (createdNode) {
                this._selectCreatedNodeIfEnabled(createdNode, true);
            }
            return createdNode;
        }

    },


    showSettingsForMusicWrapperTriggered : function(){
        return PersistentModels.getInstance().getRouter().showSequencerNodeInspectorSectionPopup(this.model, this.view.getSelectionNode());
    },


    onPlayFromTriggered : function(){
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const nodeToPlayFrom = selectionInfo.startSequencerNodeModel;
            this.model.playPauseSequencerRenderingFromNode(nodeToPlayFrom, true);
            this.view.unselectCurrentlySelectedSequencerNodes();
        }
    },

    onCommentOnSelectionTriggered : function(){
        if(this.view && typeof this.view.showCommentsPanelForCurrentSelection === "function"){
            return this.view.showCommentsPanelForCurrentSelection();
        }
        return null;
    },

    /**
     * Checks workspace transcription hour quota before allowing transcription.
     * Returns true if transcription is allowed, false if blocked.
     * For personal or ambiguous workspace context, always returns true and lets
     * the backend enforce the final quota decision.
     */
    _checkWorkspaceTranscriptionQuota : async function(){
        const workspaceId = this.getWorkspaceId();
        const activeWorkspaceId = LocalStorageHelper.getInstance().getCurrentWorkspaceId();
        if(workspaceId == null || !activeWorkspaceId || workspaceId !== activeWorkspaceId){
            return true;
        }
        try {
            const usage = await TrebbleClientAPIHelper.getInstance().getWorkspaceUsage({ workspaceId: activeWorkspaceId });
            if(usage && typeof usage.hoursUsed === "number" && typeof usage.hoursTotal === "number"){
                if(usage.hoursUsed >= usage.hoursTotal){
                    const detailTemplate = window.getI18n(ti18n, "WORKSPACE_TRANSCRIPTION_LIMIT_REACHED_DETAIL") || "Your workspace has used {hoursUsed} out of {hoursTotal} transcription hours this month. Please ask your workspace owner to upgrade the plan for more hours.";
                    const detail = detailTemplate
                        .replace("{hoursUsed}", String(usage.hoursUsed))
                        .replace("{hoursTotal}", String(usage.hoursTotal));
                    window.alertErrorMessage(
                        window.getI18n(ti18n, "WORKSPACE_TRANSCRIPTION_LIMIT_REACHED") || "Transcription limit reached",
                        detail
                    );
                    return false;
                }
            }
            return true;
        } catch(error){
            console.error("Failed to check workspace transcription quota:", error);
            // Allow transcription if quota check fails — backend will enforce
            return true;
        }
    },

    transcribedSelectedMedia : async function(){
        if(!this.hasMutationAccess()){
            return;
        }
        // Check workspace transcription quota before proceeding
        const isAllowed = await this._checkWorkspaceTranscriptionQuota();
        if(!isAllowed){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const sequencerNode = selectionInfo.startSequencerNodeModel;
            const notificationProgressUniqueId = new Date().getTime().toString();
            const onTranscriptionStarted = ()=>{
                TrebbleNotificationManager.createLoadingNotification(notificationProgressUniqueId, null, window.getI18n(ti18n, "TRANSCRIBING_ELLIPSIS"),{ duration: 0 ,darkTheme: false,  placement:"topRight"});
            };
            const onTranscriptionEnded= ()=>{

            };
            const onReplacementStarted = ()=>{
                TrebbleNotificationManager.createLoadingNotification(notificationProgressUniqueId, null, window.getI18n(ti18n, "ADDING_TRANSCRIPTION_TO_THE_EDITING_SCRIPT")+"...",{ duration: 0 ,darkTheme: false,  placement:"topRight"});
            };
            const onReplacementEnded= ()=>{
                TrebbleNotificationManager.destroyNotification(notificationProgressUniqueId);
            };
            const transcribeProxyFunction = async (languageCode)=>{
                this.view.unselectCurrentlySelectedSequencerNodes();
                //$.mobile.loading("show");
                await this.model.transcribeSequencerNode(sequencerNode, languageCode, onTranscriptionStarted, onTranscriptionEnded, onReplacementStarted, onReplacementEnded);
                //$.mobile.loading("hide");
            }
            await PersistentModels.getInstance().getRouter().showChooseLanguageForTranscriptionPopup(transcribeProxyFunction);
        }
    },

    getUserToRecord : async function(onRecordingsReady){
        return new Promise((resolve, reject)=>{
            try{
                this.view.setUnsafeToRemove(true);
                let blobsReturned = false;
                const onAllAudioRecorded = async (arrayOfBlobRecorded)=>{
                    blobsReturned = true;
                    try{
                        // The recorder awaits this callback before deleting its OPFS files.
                        const recordingsConsumed = onRecordingsReady ? await onRecordingsReady(arrayOfBlobRecorded) : undefined;
                        resolve(arrayOfBlobRecorded);
                        this.view.setUnsafeToRemove(false);
                        return recordingsConsumed;
                    }catch(error){
                        onErrorOccured(error);
                        return false;
                    }
                }
                const onPageClosed = ()=>{
                    if(!blobsReturned){
                        resolve();
                    }
                    this.view.setUnsafeToRemove(false);
                }

                const onErrorOccured = (error)=>{
                    reject(error)
                    this.view.setUnsafeToRemove(false);
                }
                PersistentModels.getInstance().getRouter().showAudioRecorderPage(onAllAudioRecorded,onPageClosed,window.getI18n(ti18n,"RECORD_AUDIO"), true).catch(onErrorOccured);
            }catch(error){
                reject(error)
                this.view.setUnsafeToRemove(false);
            }
        })

    },

    recordAndInsert : async function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const sequencerNodeTargetToInsertBefore = selectionInfo.startSequencerNodeModel;
            const audioOrVideoModelsToInsert = [];
            await this.getUserToRecord(async (blobOfAudioRecorded)=>{
                const recordedFilesStoredInLibrary = [];
                if(blobOfAudioRecorded && blobOfAudioRecorded.length > 0){
                    for(let i =0; i < blobOfAudioRecorded.length;i++){
                        const blob =  blobOfAudioRecorded[i];
                        const isVideo =  await Utils.getInstance().isVideoFile(blob);
                        const actionToExecute =  async(progressReportFunction)=>{
                            return await FileUploadHelper.getInstance().showUploadDialogAndUploadFileToSoundLibrary(blob, null, true, isVideo, true, progressReportFunction, { workspaceId: this.getWorkspaceId() });
                        }
                        const trebbleAudioOrVideJSON = await PersistentModels.getInstance().getRouter().executeFunctionAndShowProgressBarPopup (actionToExecute,`${window.getI18n(ti18n,"UPLOADING_YOUR_RECORDING")}...`);
                        if(trebbleAudioOrVideJSON){
                            if(isVideo){
                                audioOrVideoModelsToInsert.push(new TrebbleVideo(trebbleAudioOrVideJSON));
                            }else{
                                audioOrVideoModelsToInsert.push(new TrebbleAudio(trebbleAudioOrVideJSON));
                            }
                            recordedFilesStoredInLibrary.push(blob);
                        }
                    }
                }
                // Uploaded takes are safe in the library, so only failed takes stay recoverable.
                return new RecordedFilesConsumptionResult(recordedFilesStoredInLibrary);
            });
            if(audioOrVideoModelsToInsert.length){
                await this.model.insertAudioOrVideoNodesInBulk(sequencerNodeTargetToInsertBefore, true, audioOrVideoModelsToInsert);
            }
        }
    },


    deleteSequencerNode: async function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const sequencerNode = selectionInfo.startSequencerNodeModel;
            this.model.deleteSequencerNode(sequencerNode);
        }
    },

    onDeleteMusicWrapperTriggered: function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const startSequencerNodeModel = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeModel = selectionInfo.endSequencerNodeModel;
            if(startSequencerNodeModel == endSequencerNodeModel){
                this.model.deleteMusicWrapNodeCreated(startSequencerNodeModel);
                this.view.unselectCurrentlySelectedSequencerNodes();
            }
        }
    },

    onDeleteWrapNodeTriggered: function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const startSequencerNodeModel = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeModel = selectionInfo.endSequencerNodeModel;
            if(startSequencerNodeModel == endSequencerNodeModel){
                // Use centralized deletion method map from WrapNodeTypes
                const wrapType = WrapNodeTypes.getWrapNodeTypeForSequencerNode(startSequencerNodeModel);
                const deletionMethod = WrapNodeTypes.getDeletionMethod(wrapType);
                const isLegacyGenericWrapNode = typeof WrapNodeTypes.isLegacyGenericWrapSequencerNode === "function"
                    && WrapNodeTypes.isLegacyGenericWrapSequencerNode(startSequencerNodeModel);
                const legacyFallbackDeleteMethod = isLegacyGenericWrapNode
                    && this.model
                    && typeof this.model.deleteBRollWrapNodeCreated === "function"
                    ? this.model.deleteBRollWrapNodeCreated
                    : null;

                if (deletionMethod && typeof this.model[deletionMethod] === 'function') {
                    this.model[deletionMethod](startSequencerNodeModel);
                } else if (legacyFallbackDeleteMethod) {
                    console.warn('Legacy generic wrap has no explicit deletion method, falling back to deleteBRollWrapNodeCreated');
                    legacyFallbackDeleteMethod.call(this.model, startSequencerNodeModel);
                } else {
                    console.error('Unknown wrap type or missing deletion method:', wrapType);
                    return;
                }
                this.view.unselectCurrentlySelectedSequencerNodes();
            }
        }
    },

    onRevertDeleteNodeCreated : function(deletedSequencerNode){
        if(!this.hasMutationAccess()){
            return;
        }
        if(deletedSequencerNode){
            this.model.revertDeletedNodeCreation(deletedSequencerNode);
        }
    },

    onUndoOperationTriggered: function(){
        if(!this.hasMutationAccess()){
            return;
        }
        if(this.model.getHistoryManagement().canUndoOperation()){
            this.model.getHistoryManagement().undo();
        }
    },

    onRedoOperationTriggered: function(){
        if(!this.hasMutationAccess()){
            return;
        }
        if(this.model.getHistoryManagement().canRedoOperation()){
            this.model.getHistoryManagement().redo();
        }

    },

    stopPlaybackIfApplicable : function(){
        return this.model.stopPlaybackIfApplicable();
    },

    playPauseSequencer : function(){
        return this.model.playPauseSequencerRendering();
    },

    playFromPositionInMilliseconds : function(timeInMilliseconds){
        this.model.playSequencerRenderingFromPositionInMilliseconds(timeInMilliseconds);
    },

    updatePlaybackPositionInMilliseconds : function(timeInMilliseconds){
        this.model.updatePlaybackPositionInMilliseconds(timeInMilliseconds);
    },

    deleteAllFillerWords : function(){
        if(!this.hasMutationAccess()){
            return;
        }
        this.model.deleteAllFillerWords();
    },


    trimSilences : function(maxSilenceInMilliseconds){
        if(!this.hasMutationAccess()){
            return;
        }
        this.model.trimSilences(maxSilenceInMilliseconds);
    },


    setHeightMargin : function(heightMargin){
        this.view.setHeightMargin(heightMargin);
    },

    onShowHelpTriggered : function(){
        //PersistentModels.getInstance().getRouter().showHelpAndFeedback(null, true);
        PersistentModels.getInstance().getRouter().showSequencerHelpContextPopup(this.model, this.view.getShowHelpMenuBtn$el(), SequencerHelpContextOptionController);
    },



    /*onDeleteAudioSequencerNodeTriggered : function(){
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const startSequencerNodeModel = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeModel = selectionInfo.endSequencerNodeModel;
            if(startSequencerNodeModel == endSequencerNodeModel){
                this.model.deleteAudioSequencerNodeCreated(startSequencerNodeModel);
                this.view.unselectCurrentlySelectedSequencerNodes();
            }
        }
    },*/

    getHasVideoSegment : function(){
        return this.model.getHasVideoSegment();
    },

    getProjectId: function(){
        return this.model.getProjectId();
    },

    getWorkspaceId: function(){
        return this.model.getWorkspaceId();
    },

    saveProject : function(){
        if(!this.hasMutationAccess()){
            return RSVP.Promise.resolve();
        }
        return this.model.saveProject();
    },


    uploadFileAndInsertBefore : async function(file, sequencerNodeTargetToInsertBefore){
        if(!this.hasMutationAccess()){
            return;
        }
        const isVideo =  await Utils.getInstance().isVideoFile(file);
        const actionToExecute =  async(progressReportFunction)=>{
            return await FileUploadHelper.getInstance().showUploadDialogAndUploadFileToSoundLibrary(file, null, true, isVideo, true, progressReportFunction, { workspaceId: this.getWorkspaceId() });
        }
        const trebbleAudioOrVideJSON = await PersistentModels.getInstance().getRouter().executeFunctionAndShowProgressBarPopup (actionToExecute,`${window.getI18n(ti18n,"UPLOADING_YOUR_FILE")}...`);
        if(trebbleAudioOrVideJSON){
            if(isVideo){
                return this.insertVideoMode(sequencerNodeTargetToInsertBefore, true, new TrebbleVideo(trebbleAudioOrVideJSON));
            }else{
                return this.insertAudioMode(sequencerNodeTargetToInsertBefore, true, new TrebbleAudio(trebbleAudioOrVideJSON));
            }
        }
    },

    uploadFilesAndInsertBefore : async function(files, sequencerNodeTargetToInsertBefore){
        if(!this.hasMutationAccess()){
            return;
        }
        const normalizedFiles = Array.prototype.slice.call(files || []).filter(function(file) {
            return !!file;
        });
        if(normalizedFiles.length === 0){
            return;
        }
        if(normalizedFiles.length === 1){
            return this.uploadFileAndInsertBefore(normalizedFiles[0], sequencerNodeTargetToInsertBefore);
        }

        const audioOrVideoModelsToInsert = [];
        for(let i = 0; i < normalizedFiles.length; i++){
            const file = normalizedFiles[i];
            const isVideo =  await Utils.getInstance().isVideoFile(file);
            const actionToExecute =  async(progressReportFunction)=>{
                return await FileUploadHelper.getInstance().showUploadDialogAndUploadFileToSoundLibrary(file, null, true, isVideo, true, progressReportFunction, { workspaceId: this.getWorkspaceId() });
            }
            const trebbleAudioOrVideJSON = await PersistentModels.getInstance().getRouter().executeFunctionAndShowProgressBarPopup (actionToExecute,`${window.getI18n(ti18n,"UPLOADING_YOUR_FILE")}...`);
            if(trebbleAudioOrVideJSON){
                audioOrVideoModelsToInsert.push(isVideo ? new TrebbleVideo(trebbleAudioOrVideJSON) : new TrebbleAudio(trebbleAudioOrVideJSON));
            }
        }
        if(audioOrVideoModelsToInsert.length){
            return this.model.insertAudioOrVideoNodesInBulk(sequencerNodeTargetToInsertBefore, true, audioOrVideoModelsToInsert);
        }
    },

    uploadFileAndInsertAfter : async function(file, sequencerNodeTargetToInsertAfter){
        if(!this.hasMutationAccess()){
            return;
        }
        const isVideo =  await Utils.getInstance().isVideoFile(file);
        const actionToExecute =  async(progressReportFunction)=>{
            return await FileUploadHelper.getInstance().showUploadDialogAndUploadFileToSoundLibrary(file, null, true, isVideo, true, progressReportFunction, { workspaceId: this.getWorkspaceId() });
        }
        const trebbleAudioOrVideJSON = await PersistentModels.getInstance().getRouter().executeFunctionAndShowProgressBarPopup (actionToExecute,`${window.getI18n(ti18n,"UPLOADING_YOUR_FILE")}...`);
        if(trebbleAudioOrVideJSON){
            if(isVideo){
                return this.insertVideoMode(sequencerNodeTargetToInsertAfter, false, new TrebbleVideo(trebbleAudioOrVideJSON));
            }else{
                return this.insertAudioMode(sequencerNodeTargetToInsertAfter, false, new TrebbleAudio(trebbleAudioOrVideJSON));
            }
        }
    },

    uploadFileAndInsertAtTheEnd : async function(file, sequencerNodeTargetToInsertBefore){
        if(!this.hasMutationAccess()){
            return;
        }
        const isVideo =  await Utils.getInstance().isVideoFile(file);
        const actionToExecute =  async(progressReportFunction)=>{
            return await FileUploadHelper.getInstance().showUploadDialogAndUploadFileToSoundLibrary(file, null, true, isVideo, true, progressReportFunction, { workspaceId: this.getWorkspaceId() });
        }
        const trebbleAudioOrVideJSON = await PersistentModels.getInstance().getRouter().executeFunctionAndShowProgressBarPopup (actionToExecute,`${window.getI18n(ti18n,"UPLOADING_YOUR_FILE")}...`);
        if(trebbleAudioOrVideJSON){
            if(isVideo){
                return this.insertVideoMode(this.model.getLastNode(), false, new TrebbleVideo(trebbleAudioOrVideJSON));
            }else{
                return this.insertAudioMode(this.model.getLastNode(), false, new TrebbleAudio(trebbleAudioOrVideJSON));
            }
        }
    },

    uploadFileAndWrapSelection : async function(file){
        if(!this.hasMutationAccess()){
            return;
        }
        const isVideo =  await Utils.getInstance().isVideoFile(file);
        const actionToExecute =  async(progressReportFunction)=>{
            return await FileUploadHelper.getInstance().showUploadDialogAndUploadFileToSoundLibrary(file, null, true, isVideo, true, progressReportFunction, { workspaceId: this.getWorkspaceId() });
        }
        const trebbleAudioOrVideJSON = await PersistentModels.getInstance().getRouter().executeFunctionAndShowProgressBarPopup (actionToExecute,`${window.getI18n(ti18n,"UPLOADING_YOUR_FILE")}...`);
        if(trebbleAudioOrVideJSON){
            if(isVideo){
                return this.wrapSelectionWithMusic(new TrebbleVideo(trebbleAudioOrVideJSON));
            }else{
                return this.wrapSelectionWithMusic(new TrebbleAudio(trebbleAudioOrVideJSON));
            }
        }
    },

    copySelectionToClipboard : function(){
        const currentSelectionInfo =  this.view.getSelectionNode();
        if(currentSelectionInfo && currentSelectionInfo.startSequencerNodeModel && currentSelectionInfo.endSequencerNodeModel){
            if(Utils.getInstance().doesSelectedNodesContainOrphanWrapNode(currentSelectionInfo.startSequencerNodeModel, currentSelectionInfo.endSequencerNodeModel)){
                return this.view.onCopySelectionCommandeInterruptedBecauseOfWrapOrphanNode();
            }else{
                const cloneStartNode = SequencerNodeCloner.getInstance().cloneLinkedSequencerNodes(currentSelectionInfo.startSequencerNodeModel, currentSelectionInfo.endSequencerNodeModel);
                let cloneEndNode = null;
                let nextNode = cloneStartNode;
                let reachLastNode = false;
                const sequencerNodeToAdd = currentSelectionInfo.startSequencerNodeModel;
                while(!reachLastNode){
                    if(nextNode.getNext()){
                        nextNode = nextNode.getNext()
                    }else{
                        reachLastNode = true;
                        cloneEndNode = nextNode;
                    }
                }
                return this.model.updateClipboard(cloneStartNode,cloneEndNode,  true);
            }
        }else{
            //Do nothing
        }
    },
    setNoiseCancellation: function(on, doNoRecordInHistory){
        if(!this.hasMutationAccess()){
            return;
        }
        this.model.setNoiseCancellation(on,doNoRecordInHistory);
    },

    setMagicSoundEnhancer: function(on, doNoRecordInHistory){
        if(!this.hasMutationAccess()){
            return;
        }
        this.model.setMagicSoundEnhancer(on,doNoRecordInHistory);
    },

    pasteClipboardBeforeSelection :function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const currentSelectionInfo =  this.view.getSelectionNode();
        if(currentSelectionInfo && currentSelectionInfo.startSequencerNodeModel){
            return this.model.pasteClipboard(currentSelectionInfo.startSequencerNodeModel, true);
        }else{
            if(this.model.getNumberOfVisibleSequencerNodes() == 0){
                return this.model.pasteClipboard(null, true);
            }
            //Do nothing
        }
    },

    cutAndCopySelectionToClipboard : function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const currentSelectionInfo =  this.view.getSelectionNode();
        if(currentSelectionInfo && currentSelectionInfo.startSequencerNodeModel && currentSelectionInfo.endSequencerNodeModel){
            if(Utils.getInstance().doesSelectedNodesContainOrphanWrapNode(currentSelectionInfo.startSequencerNodeModel, currentSelectionInfo.endSequencerNodeModel)){
                return this.view.onCopySelectionCommandeInterruptedBecauseOfWrapOrphanNode();
            }else{
                return this.model.cutAndCopyToClipboard(currentSelectionInfo.startSequencerNodeModel, currentSelectionInfo.endSequencerNodeModel);
            }
        }else{
            //Do nothing
        }
    },

    showContextMenuForCorrectingTranscriptsIfApplicable:function(){
        if(!this.hasMutationAccess()){
            return;
        }
        return this.view.showContextMenuForCorrectingTranscriptsIfApplicable();
    },

    showAdjustTimingPopup : function(){
        if(!this.hasMutationAccess()){
            return;
        }
        return this.view.showAdjustTimingPopup();
    },

    showContextMenuForCorrectingTranscripts : function() {
        if(!this.hasMutationAccess()){
            return;
        }
        return this.view.showContextMenuForCorrectingTranscripts();
    },

    showMoreOptionMenu : function(moreButtonButton$el, onSubMenuClosed, popupPosition){
        if(!this.view || !this.view.isMoreOptionAvailable()){
            return;
        }
        const onMenuClose = (function(){
            this._moreOptionMenuController = null;
            if(onSubMenuClosed){
                onSubMenuClosed();
            }
        }).bind(this);
        const onMenuItemClicked = (function(){
            this.view.destroyContextMenuDisplayed();
        }).bind(this);
        this.view.destroyContextMenuDisplayed(true);
        return  PersistentModels.getInstance().getRouter().showSequencerNodeMoreOption(this, moreButtonButton$el, onMenuClose, onMenuItemClicked, popupPosition, SequencerNodeMoreOptionController).then((function(moreOptionMenuController){
            this._moreOptionMenuController = moreOptionMenuController
            this.view.setSubContextMenuView(this._moreOptionMenuController.getView());
        }).bind(this));
    },

    isClippingActionAvailable : function(){
        return this.view.isClippingActionAvailable();
    },

    isCreateClipActionAvailable : function(){
        return this.view.isCreateClipActionAvailable();
    },

    isCutActionAvailable : function(){
        return this.view.isCutActionAvailable();
    },

    isCopyActionAvailable : function(){
        return this.view.isCopyActionAvailable();
    },

    isPasteActionAvailable : function(){
        return this.view.isPasteActionAvailable();
    },

    isTranscriptCorrectionAvailable : function(){
        return this.view.isTranscriptCorrectionAvailable();
    },

    isAdjustTimingAvailable : function(){
        return this.view.isAdjustTimingAvailable();
    },

    isChangeSpeakerForSelectionAvailable : function(){
        return this.view.isChangeSpeakerForSelectionAvailable();
    },

    isBreakParagraphBeforeSelectionAvailable : function(){
        return this.view.isBreakParagraphBeforeSelectionAvailable();
    },

    isBreakParagraphAfterSelectionAvailable : function(){
        return this.view.isBreakParagraphAfterSelectionAvailable();
    },

    isMergeWithPreviousParagraphAvailable : function(){
        return this.view.isMergeWithPreviousParagraphAvailable();
    },

    isMergeWithNextParagraphAvailable : function(){
        return this.view.isMergeWithNextParagraphAvailable();
    },

    isDownloadClipAvailable : function(){
        return true;
    },

    isDeleteActionAvailable : function(){
        return this.view.isDeleteActionAvailable();
    },

    isWrapWithBackgroundMusicActionAvailable : function(){
        return this.view.isWrapWithBackgroundMusicActionAvailable();
    },

    isPlayFromHereActionAvailable : function(){
        return this.view.isPlayFromHereActionAvailable();
    },

    isCommentActionAvailable : function(){
        return this.view.isCommentActionAvailable();
    },

    isInsertActionAvailable : function(){
        return this.view.isInsertActionAvailable();
    },

    isMoreOptionAvailable : function(){
        return this.view.isMoreOptionAvailable();
    },

    onContextMenuDestroyed : function(){
        if(this._moreOptionMenuController){
            this._moreOptionMenuController.close();
        }
    },

    showChangeSpeakerSelectionMenu : function(){
        return this.view.showChangeSpeakerSelectionMenu();
    },

    breakParagraphBeforeSelection : function(){
        return this.view.breakParagraphBeforeSelection();
    },

    breakParagraphAfterSelection : function(){
        return this.view.breakParagraphAfterSelection();
    },

    mergeSelectionWithPreviousParagraph : function(){
        return this.view.mergeSelectionWithPreviousParagraph();
    },

    mergeSelectionWithNextParagraph : function(){
        return this.view.mergeSelectionWithNextParagraph();
    },



    onSearchTurnedOn : function(){
        this.model.setSearchOn(true);
    },

    onShowNextNodeFoundInSearch : function(){
        this.model.showNextSequencerNodeFoundInSearch();
    },

    onShowPreviousNodeFoundInSearch : function(){
        this.model.showPreviousSequencerNodeFoundInSearch();
    },

    clearSequencer: function(clearBlobCache){
        const clearSequencerPromise = this.model.clearSequencer(clearBlobCache);
        this._clearSequencerPromise = clearSequencerPromise.then((function(result){
            if(this._clearSequencerPromise === clearSequencerPromise){
                this._clearSequencerPromise = null;
            }
            return result;
        }).bind(this), (function(error){
            if(this._clearSequencerPromise === clearSequencerPromise){
                this._clearSequencerPromise = null;
            }
            throw error;
        }).bind(this));
        return this._clearSequencerPromise;
    },

    onExitCurrentMode : function(){
        if(this.model.isInSearchMode()){
            this.model.clearSearch();
        }
    },

    onSearchInputChange : async function(){
        if(this.model.isInSearchMode()){
            await this.model.applyTextSearch(this.view.getSearchInput());
        }
    },

    onTurnSearchPlaybackOn : function(){
        if(this.model.isInSearchMode()){
            this.model.setPlaybackOn(true);
        }
    },

    onTurnSearchPlaybackOff : function(){
        if(this.model.isInSearchMode()){
            this.model.setPlaybackOn(false);
        }
    },

    onPlayfromNode : function(nodeToPlayFrom, stopAfterMilliseconds, nodeToStopPlayAt, arrayOfSequencerNodesToIgnore){
        const shouldSequencerNodeBePlayedValidatorFunction = (sequencerNode, nodeSchedulingInfo)=>{
            if(arrayOfSequencerNodesToIgnore){
                return !arrayOfSequencerNodesToIgnore.includes(sequencerNode);
            }
            return true;
        }
        this.model.playPauseSequencerRenderingFromNode(nodeToPlayFrom, true, stopAfterMilliseconds, nodeToStopPlayAt, shouldSequencerNodeBePlayedValidatorFunction);
    },

    setAutoSaveOn : function(autoSaveOn){
        this.model.setAutoSaveOn(autoSaveOn);
    },


    startVideoRenderingInCloudForLaterDownload : async function(sequencerNodeToStartFrom, sequencerNodeToEndOn , audioUrlInCloud, capsuleId, useTemporaryLocation, showProgressDialog, audioEditVersionAtRender){

        let progressBarPopupController = null;
        let isVideoSegmentRendered =  false;
        const progressReportFunction = function(progress, operationInProgress){
            if(showProgressDialog){
                if(progressBarPopupController){
                    let defaultProgresMessageOnUpload = isVideoSegmentRendered? window.getI18n(ti18n, "RENDERING_VIDEO_FILE") : window.getI18n(ti18n, "RENDERING_AUDIO");
                    if(progress){
                        progressBarPopupController.setIndeterminate(false);
                        progressBarPopupController.setProgressMessage(operationInProgress?operationInProgress:defaultProgresMessageOnUpload);
                        progressBarPopupController.setProgressSubMessage(window.getI18n(ti18n, "THIS_MIGHT_TAKE_A_FEW_MINUTES")+ " "+ window.getI18n(ti18n, "DO_NOT_REFRESH")+ " "+window.getI18n(ti18n, "PLEASE_HOLD_ON_TIGHT"));
                        progressBarPopupController.setPercentageProgress(Math.round(progress)+ "%");
                    }else{
                        progressBarPopupController.setIndeterminate(true);
                        progressBarPopupController.setProgressMessage(operationInProgress?operationInProgress:defaultProgresMessageOnUpload);
                        progressBarPopupController.setProgressSubMessage(window.getI18n(ti18n, "THIS_MIGHT_TAKE_A_FEW_MINUTES")+ " "+ window.getI18n(ti18n, "DO_NOT_REFRESH")+ " "+window.getI18n(ti18n, "PLEASE_HOLD_ON_TIGHT"));
                    }
                }
            }
        }

        if(showProgressDialog){
            PersistentModels.getInstance().getRouter().showProgressBarPopupController().then(function(popupController){
                progressBarPopupController = popupController;
                progressReportFunction();

            })
        }

        if(audioUrlInCloud && audioEditVersionAtRender !== undefined){
            if(this.model.getAudioEditVersion() !== audioEditVersionAtRender){
                audioUrlInCloud = null;
            }
        }

        if(!audioUrlInCloud){
            isVideoSegmentRendered = false;
            const audioRenderedBlob = await this.model.getRenderedAudioBlobClip(true, sequencerNodeToStartFrom, sequencerNodeToEndOn, false, progressReportFunction);
            isVideoSegmentRendered = true;
            const {presignedUrl: presignedUrlForAudio, fileLocationUrl: fileLocationUrlForAudio} = await TrebbleClientAPIHelper.getInstance().getPresignedUrlForUploadingTemporaryAudioOrVideoFile("audio/wav");
            const uploadPogressFunction = (p)=>{return progressReportFunction(p*100, window.getI18n(ti18n, "PREPARING_AUDIO"))};
            await FileUploadHelper.getInstance().uploadBlobToS3UsignPreSignedUrlWithProgress(audioRenderedBlob, presignedUrlForAudio, uploadPogressFunction);
            audioUrlInCloud = fileLocationUrlForAudio;

        }
        const ffmpegParamsInfoArrayToRunOnServer = await this.createFFMPEGParamsByContatenationForServerRendering(sequencerNodeToStartFrom, sequencerNodeToEndOn,audioUrlInCloud );
        if(ffmpegParamsInfoArrayToRunOnServer && ffmpegParamsInfoArrayToRunOnServer.length > 0){
            const serverAudioUrl = audioUrlInCloud;
            if(progressReportFunction){
                progressReportFunction(null);
            }
            const srtFileContent =  await this.getSrtText(sequencerNodeToStartFrom, sequencerNodeToEndOn, null, null, false);
            const {presignedUrl: presignedUrlForFFMPGECommands, fileLocationUrl: fileLocationUrlForFFMPGECommands} = useTemporaryLocation? await TrebbleClientAPIHelper.getInstance().getPresignedUrlForUploadingTemporaryAudioOrVideoFile("application/json") : await TrebbleClientAPIHelper.getInstance().getPresignedUrlForUploadingFFMPEGCommands();
            await FileUploadHelper.getInstance().uploadJSONToS3UsignPreSignedUrl(ffmpegParamsInfoArrayToRunOnServer,presignedUrlForFFMPGECommands);
            const {presignedUrl: presignedUrlForSrt, fileLocationUrl: fileLocationUrlForSrt} = useTemporaryLocation? await TrebbleClientAPIHelper.getInstance().getPresignedUrlForUploadingTemporaryAudioOrVideoFile("text/plain") : await TrebbleClientAPIHelper.getInstance().getPresignedUrlForUploadingSrtFile();
            await FileUploadHelper.getInstance().uploadStringToS3UsignPreSignedUrl(srtFileContent,presignedUrlForSrt);
            await TrebbleClientAPIHelper.getInstance().startVideoRenderingInCloud(capsuleId, this.getProjectId(), serverAudioUrl, fileLocationUrlForSrt, useTemporaryLocation, fileLocationUrlForFFMPGECommands, this.getWorkspaceId());
            if(progressBarPopupController){
                progressBarPopupController.close();
            }
            window.alertSuccessMessage(window.getI18n(ti18n,"YOUR_VIDEO_IS_BEING_PROCESSED"),window.getI18n(ti18n, "WE_WILL_NOTIFY_WHEN_YOUR_VIDEO_IS_READY_TO_DOWNLOAD"));
            if(window.trebbleAnalyticsHelper){
                window.trebbleAnalyticsHelper.trackEvent("audioEditor", "CloudVideoRenderTriggered", "Cloud Video Render Triggered", null, {"projectId": this.getProjectId(), "capsuleId": capsuleId});
            }
        } else {
            // Handle empty FFmpeg commands - this indicates a rendering issue
            if(progressBarPopupController){
                progressBarPopupController.close();
            }
            console.error('Video rendering failed: FFmpeg command array is empty. This may be caused by concurrent rendering operations, invalid video segments, or no video content to render.');
            if(window.trebbleAnalyticsHelper){
                window.trebbleAnalyticsHelper.trackEvent("audioEditor", "CloudVideoRenderFailed", "Cloud Video Render Failed", null, {"projectId": this.getProjectId(), "capsuleId": capsuleId, "errorCategory": "empty_ffmpeg_commands"});
            }
            const videoRenderingEmptyCommandsErrorMessage = window.getI18n(ti18n, "VIDEO_RENDERING_EMPTY_COMMANDS_ERROR") || "Unable to prepare the video render. Please try again. If this keeps happening, contact support.";
            window.alertErrorMessage(
                window.getI18n(ti18n, "VIDEO_RENDERING_FAILED") || "Video rendering failed",
                videoRenderingEmptyCommandsErrorMessage
            );
            throw new Error("Video rendering failed: FFmpeg command array is empty");
        }

    },

    closeAudioContext: async function(){
        if(this._clearSequencerPromise){
            try{
                await this._clearSequencerPromise;
            }catch(error){
                console.warn('Failed to finish sequencer cleanup before closing audio context', error);
            }
        }
        return await this.getModel().closeAudioContext();
    },

    downloadSelectionAsClip: function(){
        const currentSelectionInfo = this.view.getSelectionNode();
        if(currentSelectionInfo && currentSelectionInfo.startSequencerNodeModel && currentSelectionInfo.endSequencerNodeModel){
            const hasVideoSegment = this.model.getHasVideoSegment() && window.chrome && EDIT_VIDEO_FILES;

            if(hasVideoSegment && RENDER_VIDEO_IN_CLOUD){
                // Use existing cloud video rendering for video exports
                return this.startVideoRenderingInCloudForLaterDownload(
                    currentSelectionInfo.startSequencerNodeModel,
                    currentSelectionInfo.endSequencerNodeModel,
                    null, null, true, true
                );
            }

            // Use the refactored SequencerExportHelper for audio exports
            let progressBarPopupController = null;
            const defaultProgressMessage = hasVideoSegment ?
                window.getI18n(ti18n, "RENDERING_VIDEO_FILE") :
                window.getI18n(ti18n, "RENDERING_AUDIO");

            const progressReportFunction = (progress, operationInProgress) => {
                if(progressBarPopupController){
                    if(progress){
                        progressBarPopupController.setIndeterminate(false);
                        progressBarPopupController.setProgressMessage(operationInProgress || defaultProgressMessage);
                        progressBarPopupController.setProgressSubMessage(
                            window.getI18n(ti18n, "THIS_MIGHT_TAKE_A_FEW_MINUTES") + " " +
                            window.getI18n(ti18n, "DO_NOT_REFRESH") + " " +
                            window.getI18n(ti18n, "PLEASE_HOLD_ON_TIGHT")
                        );
                        progressBarPopupController.setPercentageProgress(Math.round(progress) + "%");
                    } else {
                        progressBarPopupController.setIndeterminate(true);
                        progressBarPopupController.setProgressMessage(operationInProgress || defaultProgressMessage);
                        progressBarPopupController.setProgressSubMessage(
                            window.getI18n(ti18n, "THIS_MIGHT_TAKE_A_FEW_MINUTES") + " " +
                            window.getI18n(ti18n, "DO_NOT_REFRESH") + " " +
                            window.getI18n(ti18n, "PLEASE_HOLD_ON_TIGHT")
                        );
                    }
                }
            };

            return PersistentModels.getInstance().getRouter().showProgressBarPopupController()
                .then(popupController => {
                    progressBarPopupController = popupController;
                    progressReportFunction();

                    // Use the refactored SequencerExportHelper
                    const exportHelper = SequencerExportHelper.getInstance();
                    const projectTitle = this.model.getProjectTitle() || 'selection';
                    const exportOptions = {
                        asWav: true, // Use WAV for high quality selection exports
                        startNode: currentSelectionInfo.startSequencerNodeModel,
                        endNode: currentSelectionInfo.endSequencerNodeModel
                    };

                    return exportHelper.exportAudioFromSequencer(
                        this.model, // Pass the sequencer instance
                        projectTitle,
                        exportOptions,
                        progressReportFunction
                    );
                })
                .then(() => {
                    if(progressBarPopupController){
                        progressBarPopupController.close();
                    }
                })
                .catch(error => {
                    console.error('Export error:', error);
                    if(progressBarPopupController){
                        progressBarPopupController.close();
                    }
                    throw error;
                });
        } else {
            // No selection available
            window.alertErrorMessage(window.getI18n(ti18n, "NO_SELECTION_ERROR") || "No selection found for export");
        }
    },

    saveAsClip: function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const currentSelectionInfo =  this.view.getSelectionNode();
        let fileNameEnterredByUser =  null;
        if(currentSelectionInfo && currentSelectionInfo.startSequencerNodeModel && currentSelectionInfo.endSequencerNodeModel){
            const hasVideoSegment =  this.model.getHasVideoSegment();
            let progressBarPopupController = null;
            let projectId = null;
            const defaultProgresMessageOnUpload = hasVideoSegment? window.getI18n(ti18n, "RENDERING_VIDEO_FILE") : window.getI18n(ti18n, "RENDERING_AUDIO");
            const progressReportFunction = function(progress, operationInProgress){
                if(progressBarPopupController){
                    if(progress){
                        progressBarPopupController.setIndeterminate(false);
                        progressBarPopupController.setProgressMessage(operationInProgress?operationInProgress:defaultProgresMessageOnUpload);
                        progressBarPopupController.setProgressSubMessage(window.getI18n(ti18n, "THIS_MIGHT_TAKE_A_FEW_MINUTES")+ " "+ window.getI18n(ti18n, "DO_NOT_REFRESH")+ " "+window.getI18n(ti18n, "PLEASE_HOLD_ON_TIGHT"));
                        progressBarPopupController.setPercentageProgress(Math.round(progress)+ "%");
                    }else{
                        progressBarPopupController.setIndeterminate(true);
                        progressBarPopupController.setProgressMessage(operationInProgress?operationInProgress:defaultProgresMessageOnUpload);
                        progressBarPopupController.setProgressSubMessage(window.getI18n(ti18n, "THIS_MIGHT_TAKE_A_FEW_MINUTES")+ " "+ window.getI18n(ti18n, "DO_NOT_REFRESH")+ " "+window.getI18n(ti18n, "PLEASE_HOLD_ON_TIGHT"));
                    }
                }
            }

            return FileUploadHelper.getInstance().collectFileName().then((function(fileName){
                if(fileName){
                    fileNameEnterredByUser = fileName;
                    return PersistentModels.getInstance().getRouter().showProgressBarPopupController();
                }

            }).bind(this)).then((async function(popupController){
                if(popupController && fileNameEnterredByUser){
                    progressBarPopupController = popupController;
                    progressReportFunction();
                    projectId = await this.deleteAllExceptSelectedNodesAndSaveThenUndo(currentSelectionInfo.startSequencerNodeModel, currentSelectionInfo.endSequencerNodeModel, "Save as clip");
                }

                if(popupController && fileNameEnterredByUser){
                    return this.model.getRenderedAudioBlobClip(true, currentSelectionInfo.startSequencerNodeModel, currentSelectionInfo.endSequencerNodeModel, hasVideoSegment, progressReportFunction);
                }
            }).bind(this)).then((function(renderedBlob){
                if(progressBarPopupController){
                    progressBarPopupController.close();
                }
                if(renderedBlob && fileNameEnterredByUser){
                    const fileToUpload  = hasVideoSegment? new File([renderedBlob], new Date().getTime() +".mp4", {"type":"video/mp4"}):  new File([renderedBlob], new Date().getTime() +".mp3", {"type":"audio/mpeg"});

                    const actionToExecute =  async(progressReportFunction)=>{
                        const trebbleAudioJSON =  await FileUploadHelper.getInstance().showUploadDialogAndUploadFileToSoundLibrary(fileToUpload, fileNameEnterredByUser, true, hasVideoSegment, true, progressReportFunction, { workspaceId: this.getWorkspaceId() });
                        const trebbleAudioModel = new TrebbleAudio(trebbleAudioJSON);
                        await TrebbleClientAPIHelper.getInstance().createCapsuleFromProjectIdAndAudioUrl(fileNameEnterredByUser, projectId, trebbleAudioModel.getAudioUrl());
                        return trebbleAudioJSON;
                    }
                    return PersistentModels.getInstance().getRouter().executeFunctionAndShowProgressBarPopup (actionToExecute,`${window.getI18n(ti18n,"UPLOADING_YOUR_CLIP")}...`);
                }
            }).bind(this)).catch((function(){
                if(progressBarPopupController){
                    progressBarPopupController.close();
                }
            }).bind(this))
        }else{
            //Do nothing
        }
    },

    createClip: async function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const currentSelectionInfo =  this.view.getSelectionNode();
        if(currentSelectionInfo && currentSelectionInfo.startSequencerNodeModel && currentSelectionInfo.endSequencerNodeModel){
            const hasVideoSegment =  this.model.getHasVideoSegment();
            if(hasVideoSegment){
                const videoDataForClipCreation = await this.model.getPreparedVideoDataForClipCreation(currentSelectionInfo.startSequencerNodeModel, currentSelectionInfo.endSequencerNodeModel);
                const deleteEverythingExceptSelectionAndGetProjectIdFunction =  async ()=>{
                   return await this.deleteAllExceptSelectedNodesAndSaveThenUndo(currentSelectionInfo.startSequencerNodeModel, currentSelectionInfo.endSequencerNodeModel, "Clip Creation");
                }
                const createAudioAndSaveInCloudFunction = async(progressReportFunction)=>{
                    const audioRenderedBlob = await this.model.getRenderedAudioBlobClip(true, currentSelectionInfo.startSequencerNodeModel, currentSelectionInfo.endSequencerNodeModel, false, progressReportFunction);
                    const {presignedUrl: presignedUrlForAudio, fileLocationUrl: fileLocationUrlForAudio} = await TrebbleClientAPIHelper.getInstance().getPresignedUrlForUploadingTemporaryAudioOrVideoFile("audio/wav");
                    const uploadPogressFunction = (p)=>{return progressReportFunction(p*100, window.getI18n(ti18n, "PREPARING_AUDIO"))};
                    await FileUploadHelper.getInstance().uploadBlobToS3UsignPreSignedUrlWithProgress(audioRenderedBlob, presignedUrlForAudio, progressReportFunction? uploadPogressFunction: null);
                    return fileLocationUrlForAudio;
                }
                return await PersistentModels.getInstance().getRouter().showClipCreatorPopup(videoDataForClipCreation, deleteEverythingExceptSelectionAndGetProjectIdFunction, createAudioAndSaveInCloudFunction, this.model.getProjectId() || this.model.getLoadedProjectId(), this.getWorkspaceId());
            }
        }
    },

    showSoundEnhancerSettings : function(){
        return PersistentModels.getInstance().getRouter().showSoundEnhancerSettingsPopupContoller(this.model, this.view.getSoundEnhancerButton$el(), SoundEnhancerSettingsPopupContoller);
    },

    showAutomatedOperationsMenu: function(){
        return PersistentModels.getInstance().getRouter().showSequencerAutomatedOperationsPopup(this.model, this.view.getShowAutomatedOperationsMenuBtn$el(), SequencerAutomatedOperationsOptionController);
    },

    shouldOnboardingBeDisplayed: function(){
        return AUDIO_EDITOR_ONBOARDING && !window.isMobileBrowser && (PersistentModels.getInstance().getRouter().didSignUpOccuredInCurrentSession() ||  !LocalStorageHelper.getInstance().isAudioEditorOnboardingWasCompletedOrDismissed());
    },

    displayAIEditSuggestionsByRequestId : async function(requestId){
        return await this.model.displayAIEditSuggestionsByRequestId(requestId);
    },

    focusTurboEditorCommentById : async function(commentId, messageId){
        return await this.model.focusTurboEditorCommentById(commentId, messageId);
    },

    showOnboardingIfApplicable: function(){
        if(this.shouldOnboardingBeDisplayed() && this.view){
            this.view.showOnboardingInstruction();
        }
    },
    onOnboardingTourCompleted :function(){
        LocalStorageHelper.getInstance().setAudioEditorOnboardingWasCompletedOrDismissed();
    },

    onOnboardingTourCancelled:function(){
        LocalStorageHelper.getInstance().setAudioEditorOnboardingWasCompletedOrDismissed();
    },

    showConfigureViewPopup : function(){
        return PersistentModels.getInstance().getRouter().showSequencerViewConfigSettingsPopupContoller(this.model, this.view.getConfigureViewBtn$el(), SequencerViewConfigSettingsPopupContoller);
    },

    showProjectSettingsPopup : function(){
        return PersistentModels.getInstance().getRouter().showProjectSettingsPopup(this);
    },

    playSearchSelectionAndSurrounding: function(){
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const startSequencerNodeModel = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeModel = selectionInfo.endSequencerNodeModel;
            const firstSequencerNodeToPlay = SequencerNodeUtils.getInstance().getPreviousNWordSequencerNode(startSequencerNodeModel, 4, true);
            const lastSequencerNodeToPlay = SequencerNodeUtils.getInstance().getNextNWordSequencerNode(endSequencerNodeModel, 4, true);
            this.model.playPauseSequencerRenderingFromNode(firstSequencerNodeToPlay, true, 0, lastSequencerNodeToPlay, null);
        }
    },

    playSurroundingWithoutSelection: function(){
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const startSequencerNodeModel = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeModel = selectionInfo.endSequencerNodeModel;
            const firstSequencerNodeToPlay = SequencerNodeUtils.getInstance().getPreviousNWordSequencerNode(startSequencerNodeModel, 4, true);
            const lastSequencerNodeToPlay = SequencerNodeUtils.getInstance().getNextNWordSequencerNode(endSequencerNodeModel, 4, true);
            const arrayOfSequencerNodesToIgnore = SequencerNodeUtils.getInstance().getArrayOfSequencerNodesFromStartToEnd(startSequencerNodeModel, endSequencerNodeModel);
            const shouldSequencerNodeBePlayedValidatorFunction = (sequencerNode, nodeSchedulingInfo)=>{
                if(arrayOfSequencerNodesToIgnore){
                    return !arrayOfSequencerNodesToIgnore.includes(sequencerNode);
                }
                return true;
            }
            this.model.playPauseSequencerRenderingFromNode(firstSequencerNodeToPlay, true, 0, lastSequencerNodeToPlay, shouldSequencerNodeBePlayedValidatorFunction);
        }
    },

    deleteSearchSelection : function(){
        if(!this.hasMutationAccess()){
            return;
        }
        this.onIgnoreActionTriggered();
    },

    deleteAllSearchSelection: function(){
        if(!this.hasMutationAccess()){
            return;
        }
        this.model.deleteAllSearchResults();
    },

    restoreDeleteNode: function(){
        if(!this.hasMutationAccess()){
            return;
        }
        const selectionInfo = this.view.getSelectionNode();
        if(selectionInfo){
            const startSequencerNodeModel = selectionInfo.startSequencerNodeModel;
            const endSequencerNodeModel = selectionInfo.endSequencerNodeModel;
            if(startSequencerNodeModel == endSequencerNodeModel && Utils.getInstance().isDeletedSequencerNodeInstance(startSequencerNodeModel)){
                this.model.revertDeletedNodeCreation(startSequencerNodeModel);
                this.view.unselectCurrentlySelectedSequencerNodes();
            }
        }
    },

    shareProject : async function(){
        // Truly empty doc: no sequencer nodes at all. Don't auto-save an empty
        // project — tell the user what to add instead of silently doing nothing.
        // Uses alertUserErrorMessage (no Rollbar tracking) because this is a
        // validation case, not a system bug.
        if(this.model.isSequencerPossiblyCleared()){
            window.alertUserErrorMessage(
                window.getI18n(ti18n, "SHARE_NOTHING_TO_SHARE"),
                window.getI18n(ti18n, "SHARE_ADD_CONTENT_HINT")
            );
            return;
        }

        // Already saved — open share immediately, same behaviour as before.
        let projectId = this.model.getProjectId();
        if(projectId){
            const projectName = this.model.getProjectTitle() || '';
            PersistentModels.getInstance().getRouter().showProjectSharePopup(projectId, projectName, this);
            return;
        }

        // Unsaved but has content: save first, then open share. Reuses the
        // existing model.saveProject() autosave path — no new save plumbing.
        if(this._shareSaveInFlight){
            return;
        }
        this._shareSaveInFlight = true;
        try{
            if(this.view){
                this.view.setShareButtonLoading(true, window.getI18n(ti18n, "SHARE_SAVING"));
            }
            await this.saveProject();
            projectId = this.model.getProjectId();
            if(!projectId){
                // saveProject swallows failures and leaves projectId null —
                // surface an inline toast (no modal dead-end) using the
                // existing NotificationManager pattern used elsewhere in
                // the editor for save/enhance failures.
                this._showShareSaveFailedNotification();
                return;
            }
            const projectName = this.model.getProjectTitle() || '';
            PersistentModels.getInstance().getRouter().showProjectSharePopup(projectId, projectName, this);
        }catch(error){
            this._showShareSaveFailedNotification();
        }finally{
            if(this.view){
                this.view.setShareButtonLoading(false);
            }
            this._shareSaveInFlight = false;
        }
    },

    _showShareSaveFailedNotification : function(){
        try{
            TrebbleNotificationManager.createErrorNotification(
                "share_save_failed_" + Date.now(),
                window.getI18n(ti18n, "SHARE_SAVE_FAILED_TITLE"),
                window.getI18n(ti18n, "SHARE_SAVE_FAILED_BODY"),
                { duration: 5, darkTheme: false, placement: "topRight" }
            );
        }catch(notifyError){
            // Defensive fallback — if the React notification root is not
            // mounted for some reason, don't leave the user with no feedback.
            window.alertUserErrorMessage(
                window.getI18n(ti18n, "SHARE_SAVE_FAILED_TITLE"),
                window.getI18n(ti18n, "SHARE_SAVE_FAILED_BODY")
            );
        }
    },

    _applyGuestCapabilityGating : function(accessLevel){
        if(!this.view){
            return;
        }
        if(accessLevel === 'can_view'){
            this.setMutationAccess(false);
            this.view.hideEditToolbar();
            this.view.showGuestBanner(window.getI18n(guestTi18n, "VIEW_ONLY_BANNER_MESSAGE"));
        } else if(accessLevel === 'can_edit'){
            this.view.disableUploadButton();
            this.view.disableTranscribeButton();
            this.view.hideShareButton();
            this.view.showGuestBanner(window.getI18n(guestTi18n, "GUEST_BANNER_MESSAGE"));
        }
    },

    _checkGuestAccessAndApplyGating : function(){
        if(LocalStorageHelper.getInstance().isWorkspaceReadOnly()){
            this._applyWorkspaceReadOnlyGating();
        }

        const self = this;
        const projectId = this.model ? this.model.getProjectId() : null;
        if(!projectId){
            return;
        }

        this._setProjectPresenceTracking(projectId);
        // Non-blocking: fire and forget
        TrebbleClientAPIHelper.getInstance().getMyAccessLevelForProject({
            projectId: projectId
        }).then(function(result){
            if(result && result.accessLevel === 'can_view'){
                // Viewer mode: minimal UI regardless of how they got access
                self._applyGuestCapabilityGating('can_view');
            } else if(result && result.isGuest && result.accessLevel === 'can_edit'){
                // External guest editor: can edit but upload/transcribe restricted
                self._applyGuestCapabilityGating('can_edit');
            }
            // Workspace members with can_edit: no restrictions (full access)
            // Check if the workspace is in read-only mode (payment failed)
            if(result && result.workspaceReadOnly){
                self._applyWorkspaceReadOnlyGating();
            }
        }).catch(function(error){
            // Silently fail - guest checking should not block editor functionality
            console.warn('Guest access check failed:', error);
        });
    },

    _applyWorkspaceReadOnlyGating : function(){
        if(!this.view){
            return;
        }
        this.setMutationAccess(false);
        this.view.disableEditToolbar();
        this.view.disableUploadButton();
        this.view.disableTranscribeButton();
        // Show read-only banner with update payment message
        const bannerMessage = window.getI18n(guestTi18n, "WORKSPACE_READ_ONLY_BANNER");
        this.view.showGuestBanner(bannerMessage || "Payment failed. Update your payment method to restore editing access.");
    },

    _markProjectOpen : function(projectId){
        if(!projectId){
            return;
        }
        const self = this;

        // Mark project as open
        TrebbleClientAPIHelper.getInstance().markProjectOpen({
            projectId: projectId
        }).catch(function(error){
            console.warn('Failed to mark project open:', error);
        });

        // Start heartbeat every 60 seconds
        this._heartbeatIntervalId = setInterval(function(){
            TrebbleClientAPIHelper.getInstance().projectEditorHeartbeat({
                projectId: projectId
            }).catch(function(error){
                console.warn('Editor heartbeat failed:', error);
            });
        }, 60000);
    },

    _startConcurrentEditPolling : function(projectId){
        if(!projectId){
            return;
        }
        const self = this;

        // Poll for other editors every 30 seconds
        this._concurrentEditIntervalId = setInterval(function(){
            TrebbleClientAPIHelper.getInstance().getProjectActiveEditors({
                projectId: projectId
            }).then(function(result){
                if(!result || !result.editors || !self.view){
                    return;
                }
                if(result.editors.length > 0){
                    if(result.editors.length === 1){
                        var editorName = result.editors[0].userName || "Someone";
                        var message = editorName + " is also editing this project";
                        self.view.showGuestBanner(message);
                    } else {
                        var countMessage = result.editors.length + " others are also editing this project";
                        self.view.showGuestBanner(countMessage);
                    }
                } else {
                    // No other editors - only hide if we were showing concurrent edit banner
                    // Don't hide guest or read-only banners
                }
            }).catch(function(error){
                console.warn('Concurrent edit check failed:', error);
            });
        }, 30000);
    },

    _stopConcurrentEditPolling : function(){
        if(this._concurrentEditIntervalId){
            clearInterval(this._concurrentEditIntervalId);
            this._concurrentEditIntervalId = null;
        }
        if(this._heartbeatIntervalId){
            clearInterval(this._heartbeatIntervalId);
            this._heartbeatIntervalId = null;
        }
    },

    _setProjectPresenceTracking : function(projectId){
        if(this._activePresenceProjectId === projectId){
            return;
        }
        if(this._activePresenceProjectId){
            this._markProjectClosed(this._activePresenceProjectId);
        }else{
            this._stopConcurrentEditPolling();
        }
        this._activePresenceProjectId = null;
        if(projectId){
            this._activePresenceProjectId = projectId;
            this._startConcurrentEditPolling(projectId);
            this._markProjectOpen(projectId);
        }
    },

    _onProjectIdChange : function(projectId){
        if(projectId){
            this._checkGuestAccessAndApplyGating();
        }
    },

    _markProjectClosed : function(projectId){
        var resolvedProjectId = projectId || this._activePresenceProjectId || (this.model ? this.model.getProjectId() : null);
        if(!projectId){
            this._activePresenceProjectId = null;
        }
        if(resolvedProjectId){
            TrebbleClientAPIHelper.getInstance().markProjectClosed({
                projectId: resolvedProjectId
            }).catch(function(error){
                console.warn('Failed to mark project closed:', error);
            });
        }
        this._stopConcurrentEditPolling();
    },

    dispose : function(){
        this._markProjectClosed();
        Chaplin.Controller.prototype.dispose.call(this);
    },

    buildModelAndRenderView : function() {
        return new RSVP.Promise((function(resolve, reject) {
            try {

                const params = {};
                params.model = this.model ;
                params.sequencerController = this;
                const promiseArray = [];
                this.view = new SequencerView(params);
                this.view.setMutationAccess(this.hasMutationAccess());
                this.listenTo(this.model, "projectIdChange", this._onProjectIdChange);
                this.listenTo(this.view, "removedFromPage", this._unregisterAutomationApi);

                this.listenTo(this.view, "ignoreSelectedNodes", this.onIgnoreActionTriggered);
                this.listenTo(this.view, "wrapSelectedNodesWithMusic", this.onWrapWithMusicActionTriggered);
                this.listenTo(this.view, "wrapSelectedNodesWithMute", this.onWrapWithMuteActionTriggered);
                this.listenTo(this.view, "playFrom", this.onPlayFromTriggered);
                this.listenTo(this.view, "commentOnSelection", this.onCommentOnSelectionTriggered);
                this.listenTo(this.view, "deleteMusicWrapper", this.onDeleteMusicWrapperTriggered);
                this.listenTo(this.view, "deleteWrapNode", this.onDeleteWrapNodeTriggered);
                this.listenTo(this.view, "showSettingsForMusicWrapper", this.showSettingsForMusicWrapperTriggered);
                this.listenTo(this.view, "playPauseSequencer", this.playPauseSequencer);
                this.listenTo(this.view, "playFromPositionInMilliseconds", this.playFromPositionInMilliseconds);
                this.listenTo(this.view, "updatePlaybackPositionInMilliseconds", this.updatePlaybackPositionInMilliseconds);

                this.listenTo(this.view, "revertDeleteNodeCreated", this.onRevertDeleteNodeCreated);
                this.listenTo(this.view, "undo", this.onUndoOperationTriggered);
                this.listenTo(this.view, "redo", this.onRedoOperationTriggered);
                this.listenTo(this.view, "showHelp", this.onShowHelpTriggered);
                this.listenTo(this.view, "insertAudioNodeTriggered", this.onInsertAudioNodeTriggered);
                this.listenTo(this.view, "insertVideoNodeTriggered", this.onInsertVideoNodeTriggered);
                //this.listenTo(this.view, "deleteAudioSequencerNodeTriggered", this.onDeleteAudioSequencerNodeTriggered);
                this.listenTo(this.view, "uploadFileAndInsertBefore", this.uploadFileAndInsertBefore);
                this.listenTo(this.view, "uploadFilesAndInsertBefore", this.uploadFilesAndInsertBefore);
                this.listenTo(this.view, "uploadFileAndInsertAfter", this.uploadFileAndInsertAfter);
                this.listenTo(this.view, "uploadFileAndInsertAtTheEnd", this.uploadFileAndInsertAtTheEnd);
                this.listenTo(this.view, "uploadFileAndWrapSelection", this.uploadFileAndWrapSelection);
                this.listenTo(this.view, "saveAsClipTriggered", this.saveAsClip);
                this.listenTo(this.view, "createClipTriggered", this.createClip);
                this.listenTo(this.view, "copySelectionToClipboard", this.copySelectionToClipboard);
                this.listenTo(this.view, "pasteClipboardBeforeSelection", this.pasteClipboardBeforeSelection);
                this.listenTo(this.view, "cutAndCopySelectionToClipboard", this.cutAndCopySelectionToClipboard);
                this.listenTo(this.view, "showMoreOptionMenu", this.showMoreOptionMenu);
                this.listenTo(this.view, "onContextMenuDestroyed", this.onContextMenuDestroyed);
                this.listenTo(this.view, "searchTurnedOn", this.onSearchTurnedOn);
                this.listenTo(this.view, "showNextNodeFoundInSearch", this.onShowNextNodeFoundInSearch);
                this.listenTo(this.view, "showPreviousNodeFoundInSearch", this.onShowPreviousNodeFoundInSearch);
                this.listenTo(this.view, "exitCurrentMode", this.onExitCurrentMode);
                this.listenTo(this.view, "searchInputChange", this.onSearchInputChange);
                this.listenTo(this.view, "turnSearchPlaybackOn", this.onTurnSearchPlaybackOn);
                this.listenTo(this.view, "turnSearchPlaybackOff", this.onTurnSearchPlaybackOff);
                this.listenTo(this.view, "playfromNode", this.onPlayfromNode);
                this.listenTo(this.view, "showSoundEnhancerSettings", this.showSoundEnhancerSettings);
                this.listenTo(this.view, "showAutomatedOperationsMenu", this.showAutomatedOperationsMenu);

                this.listenTo(this.view, "onboardingTourCompleted", this.onOnboardingTourCompleted);
                this.listenTo(this.view, "onboardingTourCancelled", this.onOnboardingTourCancelled);
                this.listenTo(this.view, "showConfigureViewPopup", this.showConfigureViewPopup);
                this.listenTo(this.view, "showProjectSettingsPopup", this.showProjectSettingsPopup);
                this.listenTo(this.view, "downloadClipTriggered", this.downloadSelectionAsClip);
                this.listenTo(this.view, "playSelectionAndSurroundings", this.playSearchSelectionAndSurrounding);
                this.listenTo(this.view, "playSurroundingsWithoutSelection", this.playSurroundingWithoutSelection);
                this.listenTo(this.view, "deleteSearchSelection", this.deleteSearchSelection);
                this.listenTo(this.view, "deleteAllSearchSelection", this.deleteAllSearchSelection);
                this.listenTo(this.view, "transcribedSequencerNode", this.transcribedSelectedMedia);
                this.listenTo(this.view, "deleteSequencerNodeTriggered", this.deleteSequencerNode);
                this.listenTo(this.view, "deleteAllExceptSelectedNodes", this.deleteAllExceptSelectedNodes);
                this.listenTo(this.view, "recordAndInsertTriggered", this.recordAndInsert);
                this.listenTo(this.view, "restoreDeleteNode", this.restoreDeleteNode);
                this.listenTo(this.view, "shareProject", this.shareProject.bind(this));





                this.view.render();
                if(this._projectAccessLevel === "can_view"){
                    this._applyGuestCapabilityGating(this._projectAccessLevel);
                }
                this._registerAutomationApi();
                if(this._setTranscribedAudioBeingAddedWhenViewGetCreated){
                    this._setTranscribedAudioBeingAddedWhenViewGetCreated = false;
                    this.view.setTranscribedAudioBeingAdded(true);
                }

                this._sequencerNodeInspectorSectionController = new SequencerNodeInspectorSectionController({"sequencer":this.model, "sequencerView":this.view});
                promiseArray.push(this._sequencerNodeInspectorSectionController.buildModelAndRenderView().then((function(){
                    this.view.initSequencerNodeInspectorView(this._sequencerNodeInspectorSectionController.getView());
                    this.showOnboardingIfApplicable();
                }).bind(this)));

                // Non-blocking: check guest access and apply capability gating
                this._checkGuestAccessAndApplyGating();
                /*this._sequencerSettingsController = new SequencerSettingsController({"sequencerSettings":this.model.getSequencerSettings()});
                promiseArray.push(this._sequencerSettingsController.buildModelAndRenderView().then((function(){
                    this.view.initSequencerSettingsView(this._sequencerSettingsController.getView())
                }).bind(this)));*/

                RSVP.Promise.all(promiseArray).then(resolve).catch(reject);


                } catch (error) {
                    reject(error);
                }

            }).bind(this));

    }

});

export default SequencerController;
